// src/screens/ndp/dashboard.jsx - Complete Updated Version with All Required Changes
import React, { useState, useRef, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import ScreenHeader from "../../components/ScreenHeader";
import ProductModal from "../../components/ProductModal";
import RootSheet from "../../components/RootSheet";
import {
  LogOut, PlusCircle, List, Calendar, UserPlus, Users,
  Eye, Edit, Trash2, FileText, FileSpreadsheet,
  X, ArrowLeft, ChevronRight, Search, Upload,
  Filter, TrendingUp, ClipboardCheck, FileCheck, Truck,
  FileSearch, Package, CheckCircle, Download, Folder, FolderOpen
} from "lucide-react";
import "./dashboard.css";
import FileEntryUploader from "./FileEntryUploader";

const PAGE_SIZE = 10;
const DEFAULT_VISIBLE_PAGES = 5;

// Helper function to check if pagination should be shown
const shouldShowPagination = (totalItems) => totalItems > PAGE_SIZE;

// Tooltip Action Button Component
const ActionIconButton = ({ icon, label, onClick, colorClass }) => (
  <button className={`action-btn ${colorClass}`} onClick={onClick} style={{ position: 'relative' }}>
    {icon}
    <span className="tooltip-text">{label}</span>
  </button>
);

// Delete Button Component without hover color change
const DeleteRowButton = ({ onClick }) => (
  <button className="delete-row-btn" onClick={onClick} style={{ position: 'relative' }}>
    <Trash2 size={16} />
    <span className="tooltip-text">Delete Row</span>
  </button>
);

// Custom Pagination component that only shows when needed
const CustomPagination = ({ currentPage, totalPages, onPageChange, totalItems }) => {
  if (!shouldShowPagination(totalItems)) return null;

  return (
    <div className="pagination-container">
      <div className="pagination-summary">
        Showing {((currentPage - 1) * PAGE_SIZE) + 1} to {Math.min(currentPage * PAGE_SIZE, totalItems)} of {totalItems} entries
      </div>
      <div className="pagination-buttons">
        <button
          className="pagination-button"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          Previous
        </button>

        {Array.from({ length: Math.min(DEFAULT_VISIBLE_PAGES, totalPages) }, (_, i) => {
          let pageNum;
          if (totalPages <= DEFAULT_VISIBLE_PAGES) {
            pageNum = i + 1;
          } else if (currentPage <= 3) {
            pageNum = i + 1;
            if (pageNum > DEFAULT_VISIBLE_PAGES - 1 && i === DEFAULT_VISIBLE_PAGES - 1) {
              return (
                <button key="ellipsis-end" className="pagination-button" disabled>
                  ...
                </button>
              );
            }
          } else if (currentPage >= totalPages - 2) {
            const startPage = totalPages - DEFAULT_VISIBLE_PAGES + 1;
            pageNum = startPage + i;
            if (i === 0 && startPage > 1) {
              return (
                <button key="ellipsis-start" className="pagination-button" disabled>
                  ...
                </button>
              );
            }
          } else {
            const startPage = currentPage - 2;
            pageNum = startPage + i;
            if (i === 0 && startPage > 1) {
              return (
                <button key="ellipsis-start" className="pagination-button" disabled>
                  ...
                </button>
              );
            }
            if (i === DEFAULT_VISIBLE_PAGES - 1 && startPage + DEFAULT_VISIBLE_PAGES - 1 < totalPages) {
              return (
                <button key="ellipsis-end" className="pagination-button" disabled>
                  ...
                </button>
              );
            }
          }

          if (pageNum > totalPages || pageNum < 1) return null;

          return (
            <button
              key={pageNum}
              className={`pagination-button ${currentPage === pageNum ? 'active' : ''}`}
              onClick={() => onPageChange(pageNum)}
            >
              {pageNum}
            </button>
          );
        })}

        <button
          className="pagination-button"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default function NDPDashboard({ user, onLogout }) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("add-marketing-data");
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const { fromRoute } = useSelector(state => state.navigation || { fromRoute: null });
  const cameFromMDDashboard = fromRoute === "/managing-director";

  // Extra fields states
  const [marketingExtraFields, setMarketingExtraFields] = useState([]);
  const [followupExtraFields, setFollowupExtraFields] = useState([]);
  const [supplierExtraFields, setSupplierExtraFields] = useState([]);
  const [quotationExtraFields, setQuotationExtraFields] = useState([]);

  // Validation attempt flags
  const [marketingSubmitAttempted, setMarketingSubmitAttempted] = useState(false);
  const [followupSubmitAttempted, setFollowupSubmitAttempted] = useState(false);
  const [drawingSubmitAttempted, setDrawingSubmitAttempted] = useState(false);
  const [quotationSubmitAttempted, setQuotationSubmitAttempted] = useState(false);
  const [rfqSubmitAttempted, setRfqSubmitAttempted] = useState(false);
  const [techSubmitAttempted, setTechSubmitAttempted] = useState(false);
  const [ppapSubmitAttempted, setPpapSubmitAttempted] = useState(false);
  const [supplierSubmitAttempted, setSupplierSubmitAttempted] = useState(false);
  const [bomSubmitAttempted, setBomSubmitAttempted] = useState(false);
  const [sampleSubmitAttempted, setSampleSubmitAttempted] = useState(false);

  // Pagination states
  const [marketingPage, setMarketingPage] = useState(1);
  const [followupPage, setFollowupPage] = useState(1);
  const [supplierPage, setSupplierPage] = useState(1);
  const [drawingPage, setDrawingPage] = useState(1);
  const [quotationPage, setQuotationPage] = useState(1);
  const [quotationHistoryPage, setQuotationHistoryPage] = useState(1);
  const [rfqPage, setRfqPage] = useState(1);
  const [techPage, setTechPage] = useState(1);
  const [ppapPage, setPpapPage] = useState(1);
  const [approvedDrawingPage, setApprovedDrawingPage] = useState(1);
  const [drawingHistoryPage, setDrawingHistoryPage] = useState(1);
  const [bomPage, setBomPage] = useState(1);

  const [selectedProducts, setSelectedProducts] = useState({});
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [groupName, setGroupName] = useState("");
  const [selectedCustomersForGroup, setSelectedCustomersForGroup] = useState([]);
  const [customerGroups, setCustomerGroups] = useState([
    { id: 1, name: "Group 1", customers: ["ABC Industries"], createdAt: new Date().toISOString() }
  ]);
  const [expandedGroup, setExpandedGroup] = useState(null);
  const [editingGroup, setEditingGroup] = useState(null);
  const [editGroupName, setEditGroupName] = useState("");

  // Drawing groups
  const [drawingGroups, setDrawingGroups] = useState([
    { id: 1, name: "General Drawings" }
  ]);
  const [selectedDrawingGroup, setSelectedDrawingGroup] = useState("");
  const [newGroupName, setNewGroupName] = useState("");
  const [showNewGroupInput, setShowNewGroupInput] = useState(false);

  // Supplier documents
  const [supplierDocuments, setSupplierDocuments] = useState([]);
  const [showSupplierDocsModal, setShowSupplierDocsModal] = useState(false);
  const [currentSupplierDocs, setCurrentSupplierDocs] = useState([]);

  // Quotation history modal state
  const [showQuotationFileModal, setShowQuotationFileModal] = useState(false);
  const [currentQuotationId, setCurrentQuotationId] = useState(null);
  const [quotationAttachment, setQuotationAttachment] = useState(null);

  // RFQ auto-fetched data state (with prices)
  const [rfqFetchedData, setRfqFetchedData] = useState({
    requested: 0,
    quoted: 0,
    pending: 0,
    regrated: 0,
    loss: 0,
    requestedAmount: 0,
    quotedAmount: 0,
    pendingAmount: 0,
    regratedAmount: 0,
    lossAmount: 0
  });

  // Data states
  const [marketingData, setMarketingData] = useState([
    { id: 1, companyName: "ABC Industries", pocName: "John Doe", location: "Chennai", designation: "Manager", mobile: "9876543210", email: "john@abc.com", whatsappNumber: "9876543210", employee: "Rajesh", address: "Chennai Main Road", createdAt: new Date().toISOString() }
  ]);

  const [nextFollowups, setNextFollowups] = useState([
    { id: 1, companyName: "XYZ Corp", pocName: "Jane Smith", location: "Bangalore", designation: "Director", mobile: "9876543211", email: "jane@xyz.com", whatsappNumber: "9876543211", address: "Bangalore Main Road", followupDate: "2024-01-20", notes: "Follow up for quotation", status: "Pending" }
  ]);

  const [suppliers, setSuppliers] = useState([
    { id: 1, companyName: "SK Suppliers", pocName: "SK", username: "sk@suppliers.com", password: "123456", address: "Coimbatore", pan: "ABCDE1234F", bankDetails: "ICICI Bank", gst: "33BBBBB0000B2Z", phone: "9876543211", supplierCode: "SUP-123", documents: [], qualityDocs: [] }
  ]);

  const [selectedDrawingFiles, setSelectedDrawingFiles] = useState([]);
  const [drawingFilePreviews, setDrawingFilePreviews] = useState([]);

  const [drawings, setDrawings] = useState([]); // each drawing: { id, groupId, groupName, leads, drawings, previews, createdAt }
  const [quotations, setQuotations] = useState([
    // Sample quotation history data
    { id: 101, selectLeads: "ABC Industries", partNumber: "PN-001", partName: "Brake Pad", quotationCost: 1500, createdAt: "2025-01-10", status: "Pending", attachment: null },
    { id: 102, selectLeads: "XYZ Corp", partNumber: "PN-002", partName: "Clutch Plate", quotationCost: 2500, createdAt: "2025-01-15", status: "Accepted", attachment: null },
    { id: 103, selectLeads: "ABC Industries", partNumber: "PN-003", partName: "Engine Mount", quotationCost: 3200, createdAt: "2025-01-20", status: "Rejected", attachment: null },
  ]);
  const [rfqData, setRfqData] = useState([]);
  const [techFeasibility, setTechFeasibility] = useState([]);
  const [ppapData, setPpapData] = useState([]);
  const [bomData, setBomData] = useState([]);
  const [samplesData, setSamplesData] = useState([]);

  // BOM structured data
  const [bomEntries, setBomEntries] = useState([]);

  // PPAP hierarchical folder states
  const [expandedFolders, setExpandedFolders] = useState({});
  const [ppapForm, setPpapForm] = useState({
    customer: '',
    productId: '',
    childProductId: '',
    rawMaterialId: '',
    gauges: '',
    tools: '',
    fixtures: '',
    attachment: null,
    categories: []
  });

  const [approvedCustomers, setApprovedCustomers] = useState([
    { id: 1, name: "ABC Industries", products: [{ id: 1, name: "Product A" }, { id: 2, name: "Product B" }] },
    { id: 2, name: "XYZ Corporation", products: [{ id: 3, name: "Product C" }] }
  ]);

  const [approvedDrawings, setApprovedDrawings] = useState([
    { id: 1, customerId: 1, customerName: "ABC Industries", drawingName: "Drawing 1", attachedQuantities: ["Item 1", "Item 2"], approvedQuantities: ["Item 1"], fileUrl: "/drawing1.pdf" },
    { id: 2, customerId: 1, customerName: "ABC Industries", drawingName: "Drawing 2", attachedQuantities: ["Item 3"], approvedQuantities: [], fileUrl: "/drawing2.pdf" },
    { id: 3, customerId: 2, customerName: "XYZ Corporation", drawingName: "Drawing 1", attachedQuantities: ["Item A"], approvedQuantities: ["Item A"], fileUrl: "/drawing3.pdf" }
  ]);

  // Products with child products and raw materials (for BOM and PPAP)
  const [products, setProducts] = useState([
    {
      id: 1, name: "Product A", childProducts: [
        { id: 101, name: "Child A1", rawMaterials: [{ id: 1001, name: "Steel", quantity: 5, unit: "kg" }] },
        { id: 102, name: "Child A2", rawMaterials: [{ id: 1002, name: "Aluminum", quantity: 3, unit: "kg" }] }
      ]
    },
    {
      id: 2, name: "Product B", childProducts: [
        { id: 201, name: "Child B1", rawMaterials: [{ id: 2001, name: "Copper", quantity: 2, unit: "kg" }] }
      ]
    }
  ]);

  // Form states
  const [marketingForm, setMarketingForm] = useState({ companyName: '', address: '', location: '', pocName: '', designation: '', mobile: '', email: '', whatsappNumber: '', employee: '' });
  const [followupForm, setFollowupForm] = useState({ companyName: '', address: '', location: '', pocName: '', designation: '', mobile: '', email: '', whatsappNumber: '', followupDate: '', notes: '' });
  const [drawingForm, setDrawingForm] = useState({ leads: '', groupId: '' });
  const [quotationForm, setQuotationForm] = useState({ selectLeads: '', selectDrawing: '', partNumber: '', partName: '', partDimension: '', rawMaterial: '', rawMaterialCost: '', productionCost: '', packingCost: '', transportationCost: '', quotationCost: '', profit: '', selectQuotation: null, feasibilityRemark: '', sampleImage: null });
  const [rfqForm, setRfqForm] = useState({
    supplierList: '', customerList: '',
    noOfRfqRequested: 0, noOfRfqQuoted: 0, noOfRfqPending: 0, noOfRfqRegrated: 0, noOfRfqLoss: 0,
    requestedAmount: 0, quotedAmount: 0, pendingAmount: 0, regratedAmount: 0, lossAmount: 0
  });
  const [techForm, setTechForm] = useState({
    date: new Date().toISOString().split('T')[0], model: '', partNumber: '', customer: '', assPartName: '',
    quantityRequired: 0, quantityRequiredPerMm: 0, npOfCavityPlanned: 0, toolDesignedBy: '', isoReview: '',
    dimensions: [{ id: Date.now(), sno: 1, dimension: '', specialChar: 'No', lowerTolerance: '', upperTolerance: '', manufacturingFeasibility: '', methodOfInspection: '' }],
    rahulCommands: '', customerCommands: '', commonAgreementDate: '',
    feedbackAttachment: null, feedbackAttachmentPreview: null, revisionNumber: 0
  });
  const [supplierForm, setSupplierForm] = useState({ pocName: '', companyName: '', username: '', password: '', address: '', pan: '', bankDetails: '', gst: '', phone: '', supplierCode: 'SUP-' + Date.now(), documents: [], qualityDocs: [] });
  const [bomForm, setBomForm] = useState({
    selectedCustomer: '',
    selectedProduct: '',
    selectedChildProduct: '',
    selectedRawMaterial: '',
    quantity: 1,
    rawMaterialQty: 1,
    length: '',
    breadth: '',
    dimension: ''
  });
  const [showCreateProduct, setShowCreateProduct] = useState(false);
  const [showRootSheetModal, setShowRootSheetModal] = useState(false);
  const [productSubmitAttempted, setProductSubmitAttempted] = useState(false);
  const [categories] = useState([
    { id: 1, name: 'General' },
    { id: 2, name: 'BOM Parts' }
  ]);
  const [rawMaterials] = useState([
    { id: 1, name: 'Steel', type: 'Raw Material' },
    { id: 2, name: 'Aluminum', type: 'Raw Material' },
    { id: 3, name: 'Copper', type: 'Raw Material' }
  ]);
  const [productForm, setProductForm] = useState({
    name: '',
    code: '',
    description: '',
    category: '',
    dimension: '',
    extraDimension: '',
    totalDimension: '',
    cost: '',
    image: null,
    _3dFile: null,
    imagePreview: null,
    _3dPreview: null
  });
  const [productExtraFields, setProductExtraFields] = useState([]);
  const [rawMaterialAssignment, setRawMaterialAssignment] = useState({
    productId: null,
    rawMaterialId: '',
    quantity: 1,
    area: 0
  });
  const [sampleForm, setSampleForm] = useState({ selectDrawing: '', selectQuotation: '', feasibilityRemark: '', sampleFile: null });
  const [samplePreview, setSamplePreview] = useState(null);

  const fileInputRefs = {
    drawing: useRef(null),
    sample: useRef(null),
    techFeedback: useRef(null),
    supplierDoc: useRef(null),
    quotationAttachment: useRef(null),
    ppapAttachment: useRef(null)
  };

  // Auto-fetch RFQ data when supplier or customer changes (including price mock)
  useEffect(() => {
    if (!rfqForm.supplierList || !rfqForm.customerList) return;

    const fetchRfqData = async () => {
      // Mock API response with prices
      const mockData = {
        requested: 5,
        quoted: 3,
        pending: 2,
        regrated: 0,
        loss: 0,
        requestedAmount: 50000,
        quotedAmount: 30000,
        pendingAmount: 20000,
        regratedAmount: 0,
        lossAmount: 0
      };
      setRfqFetchedData(mockData);
      setRfqForm(prev => ({
        ...prev,
        noOfRfqRequested: mockData.requested,
        noOfRfqQuoted: mockData.quoted,
        noOfRfqPending: mockData.pending,
        noOfRfqRegrated: mockData.regrated,
        noOfRfqLoss: mockData.loss,
        requestedAmount: mockData.requestedAmount,
        quotedAmount: mockData.quotedAmount,
        pendingAmount: mockData.pendingAmount,
        regratedAmount: mockData.regratedAmount,
        lossAmount: mockData.lossAmount
      }));
    };

    fetchRfqData();
  }, [rfqForm.supplierList, rfqForm.customerList]);

  // Navigation handlers
  const handleLogoutClick = () => setShowLogoutModal(true);
  const handleConfirmLogout = () => { setShowLogoutModal(false); onLogout(); navigate('/login'); };
  const handleCancelLogout = () => setShowLogoutModal(false);
  const handleBackToMD = () => navigate("/managing-director");

  // Validation functions (updated for supplier)
  const validateMarketingForm = () => {
    const e = {};
    if (!marketingForm.companyName.trim()) e.companyName = 'Company name is required';
    if (!marketingForm.location.trim()) e.location = 'Location is required';
    if (!marketingForm.pocName.trim()) e.pocName = 'Point of contact name is required';
    if (!marketingForm.mobile.trim()) e.mobile = 'Mobile number is required';
    if (!marketingForm.email.trim()) e.email = 'Email is required';
    if (!marketingForm.address.trim()) e.address = 'Address is required';
    if (!marketingForm.employee.trim()) e.employee = 'Employee name is required';
    return e;
  };

  const validateFollowupForm = () => {
    const e = {};
    if (!followupForm.companyName.trim()) e.companyName = 'Company name is required';
    if (!followupForm.pocName.trim()) e.pocName = 'Point of contact is required';
    if (!followupForm.mobile.trim()) e.mobile = 'Mobile is required';
    if (!followupForm.followupDate) e.followupDate = 'Followup date is required';
    return e;
  };

  const validateDrawingForm = () => {
    const e = {};
    if (!drawingForm.leads) e.leads = 'Please select a lead';
    if (!drawingForm.groupId) e.groupId = 'Please select a drawing group';
    return e;
  };

  const validateQuotationForm = () => {
    const e = {};
    if (!quotationForm.selectDrawing) e.selectDrawing = 'Please select a drawing';
    if (!quotationForm.partNumber) e.partNumber = 'Part number is required';
    if (!quotationForm.partName) e.partName = 'Part name is required';
    return e;
  };

  const validateRfqForm = () => {
    const e = {};
    if (!rfqForm.supplierList) e.supplierList = 'Supplier list is required';
    if (!rfqForm.customerList) e.customerList = 'Customer list is required';
    if (!rfqForm.noOfRfqRequested || rfqForm.noOfRfqRequested <= 0) e.noOfRfqRequested = 'Valid number of RFQ requested is required';
    return e;
  };

  const validateTechForm = () => {
    const e = {};
    if (!techForm.date) e.date = 'Date is required';
    if (!techForm.model.trim()) e.model = 'Model is required';
    if (!techForm.partNumber.trim()) e.partNumber = 'Part number is required';
    if (!techForm.customer) e.customer = 'Customer is required';
    if (!techForm.quantityRequired || techForm.quantityRequired <= 0) e.quantityRequired = 'Valid quantity is required';
    if (!techForm.toolDesignedBy.trim()) e.toolDesignedBy = 'Tool designed by is required';
    if (!techForm.commonAgreementDate) e.commonAgreementDate = 'Common agreement date is required';
    if (techForm.dimensions.length === 0 || techForm.dimensions.some(d => !d.dimension)) e.dimensions = 'At least one dimension is required';
    return e;
  };

  const validateProductForm = () => {
    const e = {};
    if (!productForm.name?.trim()) e.name = 'Product Name is required';
    if (!productForm.code?.trim()) e.code = 'Product Code is required';
    if (!productForm.category) e.category = 'Category is required';
    if (!productForm.cost || productForm.cost <= 0) e.cost = 'Valid Cost is required';
    return e;
  };

  const validatePpapForm = () => {
    const e = {};
    if (!ppapForm.customer) e.customer = 'Customer is required';
    if (!ppapForm.productId) e.productId = 'Product is required';
    return e;
  };

  const validateSupplierForm = () => {
    const e = {};
    if (!supplierForm.pocName.trim()) e.pocName = 'POC Name is required';
    if (!supplierForm.companyName.trim()) e.companyName = 'Company Name is required';
    if (!supplierForm.address.trim()) e.address = 'Address is required';
    if (!supplierForm.pan.trim()) e.pan = 'PAN is required';
    if (!supplierForm.bankDetails.trim()) e.bankDetails = 'Bank Details are required';
    if (!supplierForm.gst.trim()) e.gst = 'GST is required';
    if (!supplierForm.phone.trim()) e.phone = 'Phone is required';
    // username is auto-generated, password is optional
    return e;
  };

  const validateBomForm = () => {
    const e = {};
    // All fields are optional now
    return e;
  };

  const validateSampleForm = () => {
    const e = {};
    if (!sampleForm.selectDrawing) e.selectDrawing = 'Drawing is required';
    if (!sampleForm.selectQuotation) e.selectQuotation = 'Quotation is required';
    return e;
  };

  // Error getters
  const getMErr = (f) => { if (!marketingSubmitAttempted) return null; return validateMarketingForm()[f]; };
  const getFErr = (f) => { if (!followupSubmitAttempted) return null; return validateFollowupForm()[f]; };
  const getSuppErr = (f) => { if (!supplierSubmitAttempted) return null; return validateSupplierForm()[f]; };
  const getQErr = (f) => { if (!quotationSubmitAttempted) return null; return validateQuotationForm()[f]; };
  const getDrawingErr = (f) => { if (!drawingSubmitAttempted) return null; return validateDrawingForm()[f]; };
  const getProductError = (f) => { if (!productSubmitAttempted) return null; return validateProductForm()[f]; };

  // Submit handlers (only new ones or modified)
  const handleAddMarketingData = () => {
    setMarketingSubmitAttempted(true);
    if (Object.keys(validateMarketingForm()).length > 0) return;
    setMarketingData([...marketingData, { id: Date.now(), ...marketingForm, extraFields: marketingExtraFields, createdAt: new Date().toISOString() }]);
    setMarketingForm({ companyName: '', address: '', location: '', pocName: '', designation: '', mobile: '', email: '', whatsappNumber: '', employee: '' });
    setMarketingExtraFields([]);
    setMarketingSubmitAttempted(false);
    alert('Marketing data added successfully!');
  };

  const handleAddFollowup = () => {
    setFollowupSubmitAttempted(true);
    if (Object.keys(validateFollowupForm()).length > 0) return;
    setNextFollowups([...nextFollowups, { id: Date.now(), ...followupForm, extraFields: followupExtraFields, status: 'Pending', createdAt: new Date().toISOString() }]);
    setFollowupForm({ companyName: '', address: '', location: '', pocName: '', designation: '', mobile: '', email: '', whatsappNumber: '', followupDate: '', notes: '' });
    setFollowupExtraFields([]);
    setFollowupSubmitAttempted(false);
    alert('Followup added successfully!');
  };

  const handleMoveToNPD = (followup) => {
    alert(`Moving ${followup.companyName} to NPD department`);
    setNextFollowups(nextFollowups.filter(f => f.id !== followup.id));
  };

  const handleAddDrawing = () => {
    setDrawingSubmitAttempted(true);
    if (Object.keys(validateDrawingForm()).length > 0) return;
    if (selectedDrawingFiles.length === 0) {
      alert('Please select at least one drawing');
      return;
    }
    const group = drawingGroups.find(g => g.id === parseInt(drawingForm.groupId));
    const newDrawing = {
      id: Date.now(),
      groupId: parseInt(drawingForm.groupId),
      groupName: group ? group.name : 'Unknown',
      leads: drawingForm.leads,
      drawings: selectedDrawingFiles,
      previews: drawingFilePreviews,
      createdAt: new Date().toISOString()
    };
    setDrawings([...drawings, newDrawing]);
    setDrawingForm({ leads: '', groupId: '' });
    setSelectedDrawingFiles([]);
    setDrawingFilePreviews([]);
    setDrawingSubmitAttempted(false);
    alert('Drawing added successfully!');
  };

  const calculateTotalDimension = () => {
    const dim = parseFloat(productForm.dimension) || 0;
    const extraDim = parseFloat(productForm.extraDimension) || 0;
    setProductForm(prev => ({ ...prev, totalDimension: (dim + extraDim).toString() }));
  };

  const handleCreateProduct = () => {
    setProductSubmitAttempted(true);
    if (Object.keys(validateProductForm()).length > 0) return;

    const newProduct = {
      id: Date.now(),
      ...productForm,
      childProducts: [],
      rawMaterials: [],
      extraFields: productExtraFields,
      createdAt: new Date().toISOString()
    };

    setProducts([...products, newProduct]);
    setShowCreateProduct(false);
    setProductSubmitAttempted(false);
    setProductForm({
      name: '',
      code: '',
      description: '',
      category: '',
      dimension: '',
      extraDimension: '',
      totalDimension: '',
      cost: '',
      image: null,
      _3dFile: null,
      imagePreview: null,
      _3dPreview: null
    });
    setProductExtraFields([]);
    setRawMaterialAssignment({ productId: null, rawMaterialId: '', quantity: 1, area: 0 });
    alert('Product created successfully!');
  };

  const handleAddQuotation = () => {
    setQuotationSubmitAttempted(true);
    if (Object.keys(validateQuotationForm()).length > 0) return;
    const newQuotation = { id: Date.now(), ...quotationForm, extraFields: quotationExtraFields, status: 'Pending', attachment: null, createdAt: new Date().toISOString() };
    setQuotations([...quotations, newQuotation]);
    setQuotationForm({ selectLeads: '', selectDrawing: '', partNumber: '', partName: '', partDimension: '', rawMaterial: '', rawMaterialCost: '', productionCost: '', packingCost: '', transportationCost: '', quotationCost: '', profit: '', selectQuotation: null, feasibilityRemark: '', sampleImage: null });
    setQuotationExtraFields([]);
    setQuotationSubmitAttempted(false);
    alert('Quotation added successfully!');
  };

  const handleUpdateQuotationStatus = (id, newStatus) => {
    setQuotations(quotations.map(q => q.id === id ? { ...q, status: newStatus } : q));
  };

  const handleOpenQuotationFileModal = (id) => {
    setCurrentQuotationId(id);
    setQuotationAttachment(null);
    setShowQuotationFileModal(true);
  };

  const handleUploadQuotationAttachment = () => {
    if (!quotationAttachment) {
      alert('Please select a file to attach.');
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      setQuotations(quotations.map(q => q.id === currentQuotationId ? { ...q, attachment: reader.result } : q));
      setShowQuotationFileModal(false);
      alert('File attached successfully!');
    };
    reader.readAsDataURL(quotationAttachment);
  };

  const handleAddRFQ = () => {
    setRfqSubmitAttempted(true);
    if (Object.keys(validateRfqForm()).length > 0) return;
    const newRFQ = { id: Date.now(), ...rfqForm, createdAt: new Date().toISOString() };
    setRfqData([...rfqData, newRFQ]);
    setRfqForm({
      supplierList: '', customerList: '',
      noOfRfqRequested: 0, noOfRfqQuoted: 0, noOfRfqPending: 0, noOfRfqRegrated: 0, noOfRfqLoss: 0,
      requestedAmount: 0, quotedAmount: 0, pendingAmount: 0, regratedAmount: 0, lossAmount: 0
    });
    setRfqSubmitAttempted(false);
    alert('RFQ added successfully!');
  };

  const handleAddTechFeasibility = () => {
    setTechSubmitAttempted(true);
    if (Object.keys(validateTechForm()).length > 0) return;
    const newTech = { id: Date.now(), ...techForm, createdAt: new Date().toISOString() };
    setTechFeasibility([...techFeasibility, newTech]);
    setTechForm({
      date: new Date().toISOString().split('T')[0], model: '', partNumber: '', customer: '', assPartName: '',
      quantityRequired: 0, quantityRequiredPerMm: 0, npOfCavityPlanned: 0,
      toolDesignedBy: '', isoReview: '',
      dimensions: [{ id: Date.now(), sno: 1, dimension: '', specialChar: 'No', lowerTolerance: '', upperTolerance: '', manufacturingFeasibility: '', methodOfInspection: '' }],
      rahulCommands: '', customerCommands: '', commonAgreementDate: '',
      feedbackAttachment: null, feedbackAttachmentPreview: null, revisionNumber: 0
    });
    setTechSubmitAttempted(false);
    alert('Technical Feasibility Report submitted!');
  };

  const handleAddPPAP = () => {
    setPpapSubmitAttempted(true);
    if (Object.keys(validatePpapForm()).length > 0) return;
    // Build structured PPAP entry
    const selectedProduct = products.find(p => p.id === parseInt(ppapForm.productId));
    const selectedChild = selectedProduct?.childProducts.find(c => c.id === parseInt(ppapForm.childProductId));
    const selectedRawMaterial = selectedChild?.rawMaterials.find(rm => rm.id === parseInt(ppapForm.rawMaterialId));
    const newPPAP = {
      id: Date.now(),
      customer: ppapForm.customer,
      product: selectedProduct?.name,
      childProduct: selectedChild?.name,
      rawMaterial: selectedRawMaterial?.name,
      gauges: ppapForm.gauges,
      tools: ppapForm.tools,
      fixtures: ppapForm.fixtures,
      attachment: ppapForm.attachment,
      createdAt: new Date().toISOString()
    };
    setPpapData([...ppapData, newPPAP]);
    setPpapForm({
      customer: '',
      productId: '',
      childProductId: '',
      rawMaterialId: '',
      gauges: '',
      tools: '',
      fixtures: '',
      attachment: null,
      categories: []
    });
    setExpandedFolders({});
    setPpapSubmitAttempted(false);
    alert('PPAP submitted successfully!');
  };

  const handleCreateSupplier = () => {
    setSupplierSubmitAttempted(true);
    if (Object.keys(validateSupplierForm()).length > 0) return;
    // Generate username from company name (auto)
    const autoUsername = supplierForm.companyName.toLowerCase().replace(/\s/g, '') + Date.now();
    const newSupplier = {
      id: Date.now(),
      ...supplierForm,
      username: autoUsername,
      extraFields: supplierExtraFields,
      createdAt: new Date().toISOString()
    };
    setSuppliers([...suppliers, newSupplier]);
    setSupplierForm({
      pocName: '', companyName: '', username: '', password: '', address: '', pan: '', bankDetails: '', gst: '', phone: '',
      supplierCode: 'SUP-' + Date.now(), documents: [], qualityDocs: []
    });
    setSupplierExtraFields([]);
    setSupplierSubmitAttempted(false);
    alert('Supplier created successfully! Username auto-generated.');
  };

  const handleGenerateBOM = () => {
    setBomSubmitAttempted(true);
    if (Object.keys(validateBomForm()).length > 0) return;
    // Build structured BOM entry with child product and raw material details
    const selectedCustomer = approvedCustomers.find(c => c.name === bomForm.selectedCustomer);
    const selectedProduct = products.find(p => p.name === bomForm.selectedProduct);
    const selectedChild = selectedProduct?.childProducts.find(c => c.id === parseInt(bomForm.selectedChildProduct));
    const selectedRawMaterial = selectedChild?.rawMaterials.find(rm => rm.id === parseInt(bomForm.selectedRawMaterial));
    const newEntry = {
      id: Date.now(),
      customer: bomForm.selectedCustomer,
      product: bomForm.selectedProduct,
      childProduct: selectedChild?.name,
      rawMaterial: selectedRawMaterial?.name,
      quantity: bomForm.quantity,
      rawMaterialQty: bomForm.rawMaterialQty,
      length: bomForm.length,
      breadth: bomForm.breadth,
      dimension: bomForm.dimension,
      createdAt: new Date().toISOString()
    };
    setBomEntries([...bomEntries, newEntry]);
    setBomForm({
      selectedCustomer: '', selectedProduct: '', selectedChildProduct: '', selectedRawMaterial: '',
      quantity: 1, rawMaterialQty: 1, length: '', breadth: '', dimension: ''
    });
    setBomSubmitAttempted(false);
    alert('BOM entry added successfully!');
  };

  const handleAddSample = () => {
    setSampleSubmitAttempted(true);
    if (Object.keys(validateSampleForm()).length > 0) return;
    const newSample = { id: Date.now(), ...sampleForm, samplePreview: samplePreview, createdAt: new Date().toISOString() };
    setSamplesData([...samplesData, newSample]);
    setSampleForm({ selectDrawing: '', selectQuotation: '', feasibilityRemark: '', sampleFile: null });
    setSamplePreview(null);
    setSampleSubmitAttempted(false);
    alert('Sample submitted successfully!');
  };

  const handleFileChange = (field, file) => {
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (field === 'image') {
          setProductForm(prev => ({ ...prev, image: file, imagePreview: reader.result }));
        } else if (field === '3d') {
          setProductForm(prev => ({ ...prev, _3dFile: file, _3dPreview: reader.result }));
        } else if (field === 'sample') {
          setSamplePreview(reader.result);
        } else if (field === 'techFeedback') {
          setTechForm(prev => ({ ...prev, feedbackAttachment: file, feedbackAttachmentPreview: reader.result }));
        } else if (field === 'supplierDoc') {
          setSupplierForm(prev => ({
            ...prev,
            documents: [...prev.documents, { name: file.name, data: reader.result, type: file.type }]
          }));
        } else if (field === 'supplierQualityDoc') {
          setSupplierForm(prev => ({
            ...prev,
            qualityDocs: [...prev.qualityDocs, { name: file.name, data: reader.result, type: file.type }]
          }));
        } else if (field === 'quotationAttachment') {
          setQuotationAttachment(file);
        } else if (field === 'ppapAttachment') {
          setPpapForm(prev => ({ ...prev, attachment: reader.result }));
        }
      };
      reader.readAsDataURL(file);
      if (field === 'sample') {
        setSampleForm(prev => ({ ...prev, sampleFile: file }));
      } else if (field === 'quotationAttachment') {
        // handled above
      } else if (field === 'ppapAttachment') {
        // handled above
      }
    }
  };

  const removePreview = (field, index) => {
    if (field === 'sample') {
      setSamplePreview(null);
      setSampleForm(prev => ({ ...prev, sampleFile: null }));
      if (fileInputRefs.sample?.current) {
        fileInputRefs.sample.current.value = '';
      }
    } else if (field === 'techFeedback') {
      setTechForm(prev => ({ ...prev, feedbackAttachment: null, feedbackAttachmentPreview: null }));
      if (fileInputRefs.techFeedback?.current) {
        fileInputRefs.techFeedback.current.value = '';
      }
    } else if (field === 'supplierDoc') {
      const updatedDocs = [...supplierForm.documents];
      updatedDocs.splice(index, 1);
      setSupplierForm(prev => ({ ...prev, documents: updatedDocs }));
    } else if (field === 'supplierQualityDoc') {
      const updatedQualityDocs = [...supplierForm.qualityDocs];
      updatedQualityDocs.splice(index, 1);
      setSupplierForm(prev => ({ ...prev, qualityDocs: updatedQualityDocs }));
    } else if (field === 'quotationAttachment') {
      setQuotationAttachment(null);
      if (fileInputRefs.quotationAttachment?.current) {
        fileInputRefs.quotationAttachment.current.value = '';
      }
    }
  };

  const addDimension = () => {
    setTechForm({
      ...techForm,
      dimensions: [...techForm.dimensions, {
        id: Date.now(),
        sno: techForm.dimensions.length + 1,
        dimension: '', specialChar: 'No', lowerTolerance: '',
        upperTolerance: '', manufacturingFeasibility: '', methodOfInspection: ''
      }]
    });
  };

  const deleteDimension = (index) => {
    const updatedDimensions = techForm.dimensions.filter((_, i) => i !== index);
    const renumberedDimensions = updatedDimensions.map((dim, idx) => ({ ...dim, sno: idx + 1 }));
    setTechForm({ ...techForm, dimensions: renumberedDimensions });
  };

  const updateDimension = (index, field, value) => {
    const updated = [...techForm.dimensions];
    updated[index][field] = value;
    setTechForm({ ...techForm, dimensions: updated });
  };

  const addExtraField = (type) => {
    if (type === 'marketing') setMarketingExtraFields([...marketingExtraFields, { name: '', value: '' }]);
    if (type === 'followup') setFollowupExtraFields([...followupExtraFields, { name: '', value: '' }]);
    if (type === 'supplier') setSupplierExtraFields([...supplierExtraFields, { name: '', value: '' }]);
    if (type === 'quotation') setQuotationExtraFields([...quotationExtraFields, { name: '', value: '' }]);
    if (type === 'product') setProductExtraFields([...productExtraFields, { name: '', value: '' }]);
  };

  const updateExtraField = (type, index, field, value) => {
    if (type === 'marketing') { const u = [...marketingExtraFields]; u[index][field] = value; setMarketingExtraFields(u); }
    if (type === 'followup') { const u = [...followupExtraFields]; u[index][field] = value; setFollowupExtraFields(u); }
    if (type === 'supplier') { const u = [...supplierExtraFields]; u[index][field] = value; setSupplierExtraFields(u); }
    if (type === 'quotation') { const u = [...quotationExtraFields]; u[index][field] = value; setQuotationExtraFields(u); }
    if (type === 'product') { const u = [...productExtraFields]; u[index][field] = value; setProductExtraFields(u); }
  };

  const removeExtraField = (type, index) => {
    if (type === 'marketing') setMarketingExtraFields(marketingExtraFields.filter((_, i) => i !== index));
    if (type === 'followup') setFollowupExtraFields(followupExtraFields.filter((_, i) => i !== index));
    if (type === 'supplier') setSupplierExtraFields(supplierExtraFields.filter((_, i) => i !== index));
    if (type === 'quotation') setQuotationExtraFields(quotationExtraFields.filter((_, i) => i !== index));
    if (type === 'product') setProductExtraFields(productExtraFields.filter((_, i) => i !== index));
  };

  const filteredData = (data, fields) => {
    if (!searchTerm) return data;
    return data.filter(item => fields.some(f => item[f]?.toString().toLowerCase().includes(searchTerm.toLowerCase())));
  };

  const paginate = (data, page) => data.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const formatValue = (value) => {
    if (value === undefined || value === null || value === '') return 'N/A';
    return value;
  };

  // Drawing Groups handlers
  const handleCreateDrawingGroup = () => {
    if (!newGroupName.trim()) {
      alert('Please enter a group name');
      return;
    }
    setDrawingGroups([...drawingGroups, { id: Date.now(), name: newGroupName.trim() }]);
    setNewGroupName('');
    setShowNewGroupInput(false);
  };

  // PPAP Helper Functions (simplified - we no longer need full hierarchy but keep for compatibility)
  const toggleFolder = useCallback((path) => {
    setExpandedFolders(prev => ({ ...prev, [path]: !prev[path] }));
  }, []);

  // Sidebar menu items
  const tabItems = [
    { id: "add-marketing-data", label: "Add Marketing Data", icon: <PlusCircle size={18} /> },
    { id: "list-marketing-data", label: "List marketing data", icon: <List size={18} /> },
    { id: "list-next-followup", label: "List of next follow up", icon: <Calendar size={18} /> },
    { id: "add-drawing", label: "Add Drawing", icon: <FileText size={18} /> },
    { id: "select-drawing-quotation", label: "Select Drawing & Add Quotation", icon: <FileSearch size={18} /> },
    { id: "quotation-history", label: "Quotation History", icon: <FileText size={18} /> },
    { id: "rfq-tracker", label: "Request for Quotation Tracker", icon: <TrendingUp size={18} /> },
    { id: "technical-feasibility", label: "Technical feasibility review report", icon: <ClipboardCheck size={18} /> },
    { id: "bom", label: "BOM", icon: <Package size={18} /> },
    { id: "samples", label: "Samples", icon: <Upload size={18} /> },
    { id: "ppap", label: "Ppap", icon: <FileCheck size={18} /> },
    { id: "list-approved-customer", label: "List of Approved customer", icon: <Users size={18} /> },
    { id: "create-supplier", label: "Create Supplier", icon: <UserPlus size={18} /> }
  ];

  // ==================== RENDER FUNCTIONS ====================

  const renderAddMarketingData = () => (
    <div>
      <div className="section-header">
        <h2 className="section-title">Add Marketing Data</h2>
      </div>

      <div className="form-container">
        <div className="form-grid">
          <div className="form-group">
            <label>Company name <span className="required-star">*</span></label>
            <input type="text" value={marketingForm.companyName} onChange={e => setMarketingForm({ ...marketingForm, companyName: e.target.value })} className={getMErr('companyName') ? 'error' : ''} />
            {getMErr('companyName') && <span className="error-text">{getMErr('companyName')}</span>}
          </div>
          <div className="form-group">
            <label>Location <span className="required-star">*</span></label>
            <input type="text" value={marketingForm.location} onChange={e => setMarketingForm({ ...marketingForm, location: e.target.value })} className={getMErr('location') ? 'error' : ''} />
            {getMErr('location') && <span className="error-text">{getMErr('location')}</span>}
          </div>
          <div className="form-group">
            <label>Point of Contact Name <span className="required-star">*</span></label>
            <input type="text" value={marketingForm.pocName} onChange={e => setMarketingForm({ ...marketingForm, pocName: e.target.value })} className={getMErr('pocName') ? 'error' : ''} />
            {getMErr('pocName') && <span className="error-text">{getMErr('pocName')}</span>}
          </div>
          <div className="form-group">
            <label>Designation <span className="optional-text">(Optional)</span></label>
            <input type="text" value={marketingForm.designation} onChange={e => setMarketingForm({ ...marketingForm, designation: e.target.value })} />
          </div>
          <div className="form-group">
            <label>Mobile <span className="required-star">*</span></label>
            <input type="tel" value={marketingForm.mobile} onChange={e => setMarketingForm({ ...marketingForm, mobile: e.target.value })} className={getMErr('mobile') ? 'error' : ''} />
            {getMErr('mobile') && <span className="error-text">{getMErr('mobile')}</span>}
          </div>
          <div className="form-group">
            <label>Email <span className="required-star">*</span></label>
            <input type="email" value={marketingForm.email} onChange={e => setMarketingForm({ ...marketingForm, email: e.target.value })} className={getMErr('email') ? 'error' : ''} />
            {getMErr('email') && <span className="error-text">{getMErr('email')}</span>}
          </div>
          <div className="form-group">
            <label>WhatsApp Number <span className="optional-text">(Optional)</span></label>
            <input type="tel" value={marketingForm.whatsappNumber} onChange={e => setMarketingForm({ ...marketingForm, whatsappNumber: e.target.value })} />
          </div>
          <div className="form-group">
            <label>Employee <span className="required-star">*</span></label>
            <input type="text" value={marketingForm.employee} onChange={e => setMarketingForm({ ...marketingForm, employee: e.target.value })} placeholder="Assigned employee" className={getMErr('employee') ? 'error' : ''} />
            {getMErr('employee') && <span className="error-text">{getMErr('employee')}</span>}
          </div>
        </div>

        <div className="form-group full-width" style={{ marginBottom: '16px' }}>
          <label>Address <span className="required-star">*</span></label>
          <textarea rows="3" value={marketingForm.address} onChange={e => setMarketingForm({ ...marketingForm, address: e.target.value })} className={getMErr('address') ? 'error' : ''} />
          {getMErr('address') && <span className="error-text">{getMErr('address')}</span>}
        </div>

        <div style={{ margin: '16px 0', padding: '16px', background: '#F9FAFB', borderRadius: '8px' }}>
          <label style={{ fontWeight: 600, fontSize: '13px', color: '#475569' }}>Add Extra Fields <span className="optional-text">(Optional)</span></label>
          {marketingExtraFields.map((field, index) => (
            <div key={index} style={{ display: 'flex', gap: '12px', marginTop: '10px', alignItems: 'center' }}>
              <input type="text" placeholder="Field name" value={field.name} onChange={e => updateExtraField('marketing', index, 'name', e.target.value)} style={{ flex: 1, padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '14px' }} />
              <input type="text" placeholder="Field value" value={field.value} onChange={e => updateExtraField('marketing', index, 'value', e.target.value)} style={{ flex: 1, padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '14px' }} />
              <button onClick={() => removeExtraField('marketing', index)} style={{ padding: '6px', background: '#FEE2E2', border: 'none', borderRadius: '6px', cursor: 'pointer', color: '#DC2626' }}><X size={16} /></button>
            </div>
          ))}
          <button className="add-field-btn" onClick={() => addExtraField('marketing')} style={{ marginTop: '10px' }}><PlusCircle size={14} /> Add field</button>
        </div>

        <div className="form-actions">
          <button className="btn-cancel" onClick={() => { setMarketingForm({ companyName: '', address: '', location: '', pocName: '', designation: '', mobile: '', email: '', whatsappNumber: '', employee: '' }); setMarketingExtraFields([]); setMarketingSubmitAttempted(false); }}>Cancel</button>
          <button className="btn-submit" onClick={handleAddMarketingData}>Submit</button>
        </div>
      </div>

      {marketingData.length > 0 && (
        <div className="list-section">
          <h3>Recent Marketing Data</h3>
          <div className="data-cards">
            {marketingData.slice(-4).reverse().map(data => (
              <div key={data.id} className="data-card">
                <div className="data-card-header">
                  <strong>{formatValue(data.companyName)}</strong>
                  <button className="view-btn-small" onClick={() => alert(JSON.stringify(data, null, 2))}>View</button>
                </div>
                <div className="data-card-details">
                  <p><span>POC:</span> {formatValue(data.pocName)}</p>
                  <p><span>Location:</span> {formatValue(data.location)}</p>
                  <p><span>Mobile:</span> {formatValue(data.mobile)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const renderListMarketingData = () => {
    const filtered = filteredData(marketingData, ['companyName', 'pocName', 'location', 'email']);
    const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
    const pageData = paginate(filtered, marketingPage);
    return (
      <div>
        <div className="section-header">
          <h2 className="section-title">Marketing Data</h2>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <button className="export-btn pdf" onClick={() => alert('Exporting to PDF...')}><FileText size={16} /> PDF</button>
            <button className="export-btn excel" onClick={() => alert('Exporting to Excel...')}><FileSpreadsheet size={16} /> Excel</button>
          </div>
        </div>
        {marketingData.length === 0 ? (
          <div className="empty-state"><Users size={48} color="#CBD5E1" /><p>No marketing data available</p></div>
        ) : (
          <>
            <div className="data-table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Company</th><th>Address</th><th>Location</th><th>POC</th><th>Designation</th>
                    <th>Mobile</th><th>Email</th><th>WhatsApp</th><th>Employee</th><th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pageData.map(data => (
                    <tr key={data.id}>
                      <td className="highlight">{formatValue(data.companyName)}</td>
                      <td>{formatValue(data.address)}</td>
                      <td>{formatValue(data.location)}</td>
                      <td>{formatValue(data.pocName)}</td>
                      <td>{formatValue(data.designation)}</td>
                      <td>{formatValue(data.mobile)}</td>
                      <td>{formatValue(data.email)}</td>
                      <td>{formatValue(data.whatsappNumber)}</td>
                      <td>{formatValue(data.employee)}</td>
                      <td className="action-buttons">
                        <ActionIconButton icon={<Eye size={16} />} label="View" onClick={() => alert(JSON.stringify(data, null, 2))} colorClass="view" />
                        <ActionIconButton icon={<Edit size={16} />} label="Edit" onClick={() => alert('Edit feature coming soon')} colorClass="edit" />
                        <ActionIconButton icon={<Trash2 size={16} />} label="Delete" onClick={() => { if (window.confirm('Delete?')) setMarketingData(marketingData.filter(d => d.id !== data.id)); }} colorClass="delete" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <CustomPagination currentPage={marketingPage} totalPages={totalPages} onPageChange={setMarketingPage} totalItems={filtered.length} />
          </>
        )}
      </div>
    );
  };

  const renderListNextFollowup = () => {
    const filtered = filteredData(nextFollowups, ['companyName', 'pocName', 'location']);
    const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
    const pageData = paginate(filtered, followupPage);

    // Auto-fill when company is selected
    const handleCompanyChange = (e) => {
      const selectedCompanyName = e.target.value;
      const selectedLead = marketingData.find(lead => lead.companyName === selectedCompanyName);
      setFollowupForm({
        ...followupForm,
        companyName: selectedCompanyName,
        pocName: selectedLead?.pocName || '',
        location: selectedLead?.location || '',
        mobile: selectedLead?.mobile || '',
        email: selectedLead?.email || '',
        address: selectedLead?.address || '',
        designation: selectedLead?.designation || '',
        whatsappNumber: selectedLead?.whatsappNumber || ''
      });
    };

    return (
      <div>
        <div className="section-header">
          <h2 className="section-title">List of Next Followup</h2>
        </div>

        <div className="form-container">
          <div className="form-grid">
            <div className="form-group">
              <label>Company name <span className="required-star">*</span></label>
              <select
                value={followupForm.companyName}
                onChange={handleCompanyChange}
                className={getFErr('companyName') ? 'error' : ''}
              >
                <option value="">Select Company</option>
                {marketingData.map(lead => (
                  <option key={lead.id} value={lead.companyName}>{lead.companyName}</option>
                ))}
              </select>
              {getFErr('companyName') && <span className="error-text">{getFErr('companyName')}</span>}
            </div>
            <div className="form-group">
              <label>Location <span className="optional-text">(Optional)</span></label>
              <input type="text" value={followupForm.location} readOnly disabled className="auto-field" />
            </div>
            <div className="form-group">
              <label>Point of Contact Name <span className="required-star">*</span></label>
              <input type="text" value={followupForm.pocName} readOnly disabled className="auto-field" />
            </div>
            <div className="form-group">
              <label>Designation <span className="optional-text">(Optional)</span></label>
              <input type="text" value={followupForm.designation} readOnly disabled className="auto-field" />
            </div>
            <div className="form-group">
              <label>Mobile <span className="required-star">*</span></label>
              <input type="tel" value={followupForm.mobile} readOnly disabled className="auto-field" />
            </div>
            <div className="form-group">
              <label>Email <span className="optional-text">(Optional)</span></label>
              <input type="email" value={followupForm.email} readOnly disabled className="auto-field" />
            </div>
            <div className="form-group">
              <label>WhatsApp Number <span className="optional-text">(Optional)</span></label>
              <input type="tel" value={followupForm.whatsappNumber} readOnly disabled className="auto-field" />
            </div>
            <div className="form-group">
              <label>Followup Date <span className="required-star">*</span></label>
              <input type="date" value={followupForm.followupDate} onChange={e => setFollowupForm({ ...followupForm, followupDate: e.target.value })} className={getFErr('followupDate') ? 'error' : ''} />
              {getFErr('followupDate') && <span className="error-text">{getFErr('followupDate')}</span>}
            </div>
          </div>

          <div className="form-group full-width" style={{ marginBottom: '16px' }}>
            <label>Address <span className="optional-text">(Optional)</span></label>
            <textarea rows="3" value={followupForm.address} readOnly disabled className="auto-field" />
          </div>

          <div className="form-group full-width" style={{ marginBottom: '16px' }}>
            <label>Notes <span className="optional-text">(Optional)</span></label>
            <textarea rows="2" value={followupForm.notes} onChange={e => setFollowupForm({ ...followupForm, notes: e.target.value })} />
          </div>

          <div style={{ margin: '16px 0', padding: '16px', background: '#F9FAFB', borderRadius: '8px' }}>
            <label style={{ fontWeight: 600, fontSize: '13px', color: '#475569' }}>Add Extra Fields <span className="optional-text">(Optional)</span></label>
            {followupExtraFields.map((field, index) => (
              <div key={index} style={{ display: 'flex', gap: '12px', marginTop: '10px', alignItems: 'center' }}>
                <input type="text" placeholder="Field name" value={field.name} onChange={e => updateExtraField('followup', index, 'name', e.target.value)} style={{ flex: 1, padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '14px' }} />
                <input type="text" placeholder="Field value" value={field.value} onChange={e => updateExtraField('followup', index, 'value', e.target.value)} style={{ flex: 1, padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '14px' }} />
                <button onClick={() => removeExtraField('followup', index)} style={{ padding: '6px', background: '#FEE2E2', border: 'none', borderRadius: '6px', cursor: 'pointer', color: '#DC2626' }}><X size={16} /></button>
              </div>
            ))}
            <button className="add-field-btn" onClick={() => addExtraField('followup')} style={{ marginTop: '10px' }}><PlusCircle size={14} /> Add field</button>
          </div>

          <div className="form-actions">
            <button className="btn-cancel" onClick={() => { setFollowupForm({ companyName: '', address: '', location: '', pocName: '', designation: '', mobile: '', email: '', whatsappNumber: '', followupDate: '', notes: '' }); setFollowupExtraFields([]); setFollowupSubmitAttempted(false); }}>Cancel</button>
            <button className="btn-submit" onClick={handleAddFollowup}>Add Followup</button>
          </div>
        </div>

        {nextFollowups.length === 0 ? (
          <div className="empty-state"><Calendar size={48} color="#CBD5E1" /><p>No followups scheduled</p></div>
        ) : (
          <>
            <div className="data-table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Company</th><th>Address</th><th>Location</th><th>POC</th><th>Designation</th>
                    <th>Mobile</th><th>Email</th><th>WhatsApp</th><th>Followup Date</th><th>Status</th><th>Notes</th><th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pageData.map(followup => (
                    <tr key={followup.id}>
                      <td className="highlight">{formatValue(followup.companyName)}</td>
                      <td>{formatValue(followup.address)}</td>
                      <td>{formatValue(followup.location)}</td>
                      <td>{formatValue(followup.pocName)}</td>
                      <td>{formatValue(followup.designation)}</td>
                      <td>{formatValue(followup.mobile)}</td>
                      <td>{formatValue(followup.email)}</td>
                      <td>{formatValue(followup.whatsappNumber)}</td>
                      <td>{formatValue(followup.followupDate)}</td>
                      <td><span className={`status-badge ${followup.status === 'Pending' ? 'pending' : 'approved'}`}>{formatValue(followup.status)}</span></td>
                      <td>{formatValue(followup.notes)}</td>
                      <td className="action-buttons">
                        <button className="move-to-npd-btn" onClick={() => handleMoveToNPD(followup)} style={{ position: 'relative' }}>
                          <ChevronRight size={14} /> NPD
                          <span className="tooltip-text">Move to NPD Department</span>
                        </button>
                        <ActionIconButton icon={<Edit size={16} />} label="Edit" onClick={() => alert('Edit feature coming soon')} colorClass="edit" />
                        <ActionIconButton icon={<Trash2 size={16} />} label="Delete" onClick={() => { if (window.confirm('Delete?')) setNextFollowups(nextFollowups.filter(f => f.id !== followup.id)); }} colorClass="delete" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <CustomPagination currentPage={followupPage} totalPages={totalPages} onPageChange={setFollowupPage} totalItems={filtered.length} />
          </>
        )}
      </div>
    );
  };

  const renderAddDrawing = () => {
    const handleMultipleFilesChange = (e) => {
      const files = Array.from(e.target.files);
      setSelectedDrawingFiles(files);
      files.forEach(file => {
        if (file.type.startsWith('image/')) {
          const reader = new FileReader();
          reader.onloadend = () => {
            setDrawingFilePreviews(prev => [...prev, reader.result]);
          };
          reader.readAsDataURL(file);
        } else {
          setDrawingFilePreviews(prev => [...prev, null]);
        }
      });
    };

    const removeFile = (index) => {
      const newFiles = selectedDrawingFiles.filter((_, i) => i !== index);
      const newPreviews = drawingFilePreviews.filter((_, i) => i !== index);
      setSelectedDrawingFiles(newFiles);
      setDrawingFilePreviews(newPreviews);
      if (fileInputRefs.drawing?.current) {
        fileInputRefs.drawing.current.value = '';
      }
    };

    // Drawing History Table
    const filteredDrawings = filteredData(drawings, ['leads', 'groupName']);
    const totalDrawingPages = Math.ceil(filteredDrawings.length / PAGE_SIZE);
    const pageDrawings = paginate(filteredDrawings, drawingHistoryPage);

    return (
      <div>
        <div className="section-header">
          <h2 className="section-title">Add Drawing</h2>
        </div>

        <div className="form-container">
          <div className="form-group full-width">
            <label>Select Leads <span className="required-star">*</span></label>
            <select value={drawingForm.leads} onChange={(e) => setDrawingForm({ ...drawingForm, leads: e.target.value })}>
              <option value="">Select Lead</option>
              {marketingData.map(d => <option key={d.id} value={d.companyName}>{d.companyName}</option>)}
            </select>
            {getDrawingErr('leads') && <span className="error-text">{getDrawingErr('leads')}</span>}
          </div>

          <div className="form-group full-width">
            <label>Drawing Group <span className="required-star">*</span></label>
            <select value={drawingForm.groupId} onChange={(e) => setDrawingForm({ ...drawingForm, groupId: e.target.value })}>
              <option value="">Select Group</option>
              {drawingGroups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
            </select>
            <div style={{ marginTop: '8px' }}>
              {!showNewGroupInput ? (
                <button type="button" className="add-field-btn" onClick={() => setShowNewGroupInput(true)}>
                  <PlusCircle size={14} /> Create New Group
                </button>
              ) : (
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <input
                    type="text"
                    placeholder="New group name"
                    value={newGroupName}
                    onChange={(e) => setNewGroupName(e.target.value)}
                    style={{ flex: 1, padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: '6px' }}
                  />
                  <button type="button" className="btn-submit" onClick={handleCreateDrawingGroup}>Save</button>
                  <button type="button" className="btn-cancel" onClick={() => setShowNewGroupInput(false)}>Cancel</button>
                </div>
              )}
            </div>
            {getDrawingErr('groupId') && <span className="error-text">{getDrawingErr('groupId')}</span>}
          </div>

          <div className="drawing-upload">
            <label>📄 Add Drawings <span className="optional-text">(Multiple files allowed)</span></label>
            <input
              type="file"
              ref={fileInputRefs.drawing}
              accept="image/*,.pdf"
              multiple
              onChange={handleMultipleFilesChange}
            />
            <small style={{ color: '#6B7280', fontSize: '11px' }}>You can select multiple drawings at once</small>
          </div>

          {drawingFilePreviews.length > 0 && (
            <div className="drawing-preview">
              {selectedDrawingFiles.map((file, index) => (
                <div key={index} className="preview-image">
                  {drawingFilePreviews[index] ? (
                    <img src={drawingFilePreviews[index]} alt={`Preview ${index + 1}`} />
                  ) : (
                    <div style={{
                      width: '80px',
                      height: '80px',
                      background: '#E0E7FF',
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '10px',
                      textAlign: 'center',
                      padding: '4px',
                      wordBreak: 'break-all'
                    }}>
                      {file.name.length > 15 ? file.name.substring(0, 12) + '...' : file.name}
                    </div>
                  )}
                  <button className="remove-preview" onClick={() => removeFile(index)}>×</button>
                </div>
              ))}
            </div>
          )}

          <div className="form-actions">
            <button className="btn-cancel" onClick={() => { setDrawingForm({ leads: '', groupId: '' }); setSelectedDrawingFiles([]); setDrawingFilePreviews([]); setDrawingSubmitAttempted(false); }}>Cancel</button>
            <button className="btn-submit" onClick={handleAddDrawing}>Submit</button>
          </div>
        </div>

        {/* Drawing History Table */}
        <div className="list-section">
          <h3>Drawing History</h3>
          {drawings.length === 0 ? (
            <div className="empty-state"><FileText size={48} color="#CBD5E1" /><p>No drawings added yet</p></div>
          ) : (
            <>
              <div className="data-table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Date</th><th>Lead</th><th>Group</th><th>Files Count</th><th>Preview</th><th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageDrawings.map(drawing => (
                      <tr key={drawing.id}>
                        <td>{new Date(drawing.createdAt).toLocaleDateString()}</td>
                        <td className="highlight">{drawing.leads}</td>
                        <td>{drawing.groupName}</td>
                        <td>{drawing.drawings.length}</td>
                        <td>
                          <div className="drawing-preview">
                            {drawing.previews && drawing.previews.slice(0, 2).map((preview, idx) => (
                              preview && <img key={idx} src={preview} style={{ width: '40px', height: '40px', objectFit: 'cover', borderRadius: '4px' }} alt="drawing" />
                            ))}
                          </div>
                        </td>
                        <td className="action-buttons">
                          <ActionIconButton icon={<Eye size={16} />} label="View Details" onClick={() => alert(JSON.stringify(drawing, null, 2))} colorClass="view" />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <CustomPagination currentPage={drawingHistoryPage} totalPages={totalDrawingPages} onPageChange={setDrawingHistoryPage} totalItems={filteredDrawings.length} />
            </>
          )}
        </div>
      </div>
    );
  };

  const renderSelectDrawingQuotation = () => {
    const filtered = filteredData(quotations, ['partNumber', 'partName']);
    const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
    const pageData = paginate(filtered, quotationPage);
    return (
      <div>
        <div className="section-header">
          <h2 className="section-title">Select Drawing & Add Quotation</h2>
        </div>

        <div className="form-container">
          <div className="form-grid">
            <div className="form-group">
              <label>Select Leads <span className="optional-text">(Optional)</span></label>
              <select value={quotationForm.selectLeads} onChange={(e) => setQuotationForm({ ...quotationForm, selectLeads: e.target.value })}>
                <option value="">Select Lead</option>
                {marketingData.map(d => <option key={d.id} value={d.companyName}>{d.companyName}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Select Drawing <span className="required-star">*</span></label>
              <select value={quotationForm.selectDrawing} onChange={(e) => setQuotationForm({ ...quotationForm, selectDrawing: e.target.value })}>
                <option value="">Select Drawing</option>
                {drawings.map(d => <option key={d.id}>{d.groupName} - {d.leads} (ID: {d.id})</option>)}
              </select>
              {getQErr('selectDrawing') && <span className="error-text">{getQErr('selectDrawing')}</span>}
            </div>
            <div className="form-group">
              <label>Part Number <span className="required-star">*</span></label>
              <input type="text" value={quotationForm.partNumber} onChange={e => setQuotationForm({ ...quotationForm, partNumber: e.target.value })} className={getQErr('partNumber') ? 'error' : ''} />
              {getQErr('partNumber') && <span className="error-text">{getQErr('partNumber')}</span>}
            </div>
            <div className="form-group">
              <label>Part Name <span className="required-star">*</span></label>
              <input type="text" value={quotationForm.partName} onChange={e => setQuotationForm({ ...quotationForm, partName: e.target.value })} className={getQErr('partName') ? 'error' : ''} />
              {getQErr('partName') && <span className="error-text">{getQErr('partName')}</span>}
            </div>
            <div className="form-group">
              <label>Part Dimension</label>
              <input type="text" value={quotationForm.partDimension} onChange={e => setQuotationForm({ ...quotationForm, partDimension: e.target.value })} />
            </div>
            <div className="form-group">
              <label>Raw Material</label>
              <input type="text" value={quotationForm.rawMaterial} onChange={e => setQuotationForm({ ...quotationForm, rawMaterial: e.target.value })} />
            </div>
            <div className="form-group">
              <label>Raw Material cost</label>
              <input type="number" value={quotationForm.rawMaterialCost} onChange={e => setQuotationForm({ ...quotationForm, rawMaterialCost: e.target.value })} />
            </div>
            <div className="form-group">
              <label>Production Cost</label>
              <input type="number" value={quotationForm.productionCost} onChange={e => setQuotationForm({ ...quotationForm, productionCost: e.target.value })} />
            </div>
            <div className="form-group">
              <label>Packing cost</label>
              <input type="number" value={quotationForm.packingCost} onChange={e => setQuotationForm({ ...quotationForm, packingCost: e.target.value })} />
            </div>
            <div className="form-group">
              <label>Transportation Cost</label>
              <input type="number" value={quotationForm.transportationCost} onChange={e => setQuotationForm({ ...quotationForm, transportationCost: e.target.value })} />
            </div>
            <div className="form-group">
              <label>Quotation Cost</label>
              <input type="number" value={quotationForm.quotationCost} onChange={e => setQuotationForm({ ...quotationForm, quotationCost: e.target.value })} />
            </div>
            <div className="form-group">
              <label>Profit</label>
              <input type="number" value={quotationForm.profit} onChange={e => setQuotationForm({ ...quotationForm, profit: e.target.value })} />
            </div>
          </div>

          <div className="form-group full-width" style={{ marginBottom: '16px' }}>
            <label>Feasibility Remark</label>
            <textarea rows="3" value={quotationForm.feasibilityRemark} onChange={e => setQuotationForm({ ...quotationForm, feasibilityRemark: e.target.value })} />
          </div>

          <div className="form-group" style={{ marginBottom: '16px' }}>
            <label>Upload sample image/pdf</label>
            <input type="file" accept=".pdf,.jpg,.png" />
          </div>

          <div style={{ margin: '16px 0', padding: '16px', background: '#F9FAFB', borderRadius: '8px' }}>
            <label style={{ fontWeight: 600, fontSize: '13px', color: '#475569' }}>Add Extra Fields <span className="optional-text">(Optional)</span></label>
            {quotationExtraFields.map((field, index) => (
              <div key={index} style={{ display: 'flex', gap: '12px', marginTop: '10px', alignItems: 'center' }}>
                <input type="text" placeholder="Field name" value={field.name} onChange={e => updateExtraField('quotation', index, 'name', e.target.value)} style={{ flex: 1, padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '14px' }} />
                <input type="text" placeholder="Field value" value={field.value} onChange={e => updateExtraField('quotation', index, 'value', e.target.value)} style={{ flex: 1, padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '14px' }} />
                <button onClick={() => removeExtraField('quotation', index)} style={{ padding: '6px', background: '#FEE2E2', border: 'none', borderRadius: '6px', cursor: 'pointer', color: '#DC2626' }}><X size={16} /></button>
              </div>
            ))}
            <button className="add-field-btn" onClick={() => addExtraField('quotation')} style={{ marginTop: '10px' }}><PlusCircle size={14} /> Add field</button>
          </div>

          <div className="form-actions">
            <button className="btn-cancel" onClick={() => { setQuotationForm({ selectLeads: '', selectDrawing: '', partNumber: '', partName: '', partDimension: '', rawMaterial: '', rawMaterialCost: '', productionCost: '', packingCost: '', transportationCost: '', quotationCost: '', profit: '', selectQuotation: null, feasibilityRemark: '', sampleImage: null }); setQuotationExtraFields([]); setQuotationSubmitAttempted(false); }}>Cancel</button>
            <button className="btn-submit" onClick={handleAddQuotation}>Submit</button>
          </div>
        </div>
      </div>
    );
  };

  const renderQuotationHistory = () => {
    const filtered = filteredData(quotations, ['partNumber', 'partName', 'selectLeads']);
    const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
    const pageData = paginate(filtered, quotationHistoryPage);

    return (
      <div>
        <div className="section-header">
          <h2 className="section-title">Quotation History</h2>
          <div className="ndp-search-wrapper">
            <div className="ndp-search-box">
              <Search size={16} />
              <input type="text" placeholder="Search quotations..." className="ndp-search-input" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
            </div>
          </div>
        </div>
        {quotations.length === 0 ? (
          <div className="empty-state"><FileText size={48} color="#CBD5E1" /><p>No quotations added yet</p></div>
        ) : (
          <>
            <div className="data-table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Date</th><th>Lead</th><th>Part Number</th><th>Part Name</th><th>Quotation Cost</th><th>Status</th><th>Attachment</th><th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pageData.map(q => (
                    <tr key={q.id}>
                      <td>{new Date(q.createdAt).toLocaleDateString()}</td>
                      <td className="highlight">{q.selectLeads}</td>
                      <td>{q.partNumber}</td>
                      <td>{q.partName}</td>
                      <td>₹{q.quotationCost}</td>
                      <td>
                        <select
                          value={q.status}
                          onChange={(e) => handleUpdateQuotationStatus(q.id, e.target.value)}
                          style={{ padding: '4px 8px', borderRadius: '4px', border: '1px solid #cbd5e1' }}
                        >
                          <option value="Pending">Pending</option>
                          <option value="Accepted">Accepted</option>
                          <option value="Rejected">Rejected</option>
                          <option value="Requoted">Requoted</option>
                        </select>
                      </td>
                      <td>
                        {q.attachment ? (
                          <a href={q.attachment} download="quotation_file" className="action-btn view" style={{ width: 'auto', padding: '4px 12px' }}>Download</a>
                        ) : (
                          <span className="optional-text">No file</span>
                        )}
                      </td>
                      <td className="action-buttons">
                        <ActionIconButton icon={<Upload size={16} />} label="Record Attachment" onClick={() => handleOpenQuotationFileModal(q.id)} colorClass="edit" />
                        <ActionIconButton icon={<Eye size={16} />} label="View Details" onClick={() => alert(JSON.stringify(q, null, 2))} colorClass="view" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <CustomPagination currentPage={quotationHistoryPage} totalPages={totalPages} onPageChange={setQuotationHistoryPage} totalItems={filtered.length} />
          </>
        )}

        {/* Quotation file attachment modal */}
        {showQuotationFileModal && (
          <div className="logout-modal-overlay" onClick={() => setShowQuotationFileModal(false)}>
            <div className="logout-modal" style={{ maxWidth: '500px' }} onClick={(e) => e.stopPropagation()}>
              <div className="logout-modal-header" style={{ background: 'linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)' }}>
                <div className="logout-modal-icon">📎</div>
                <h3>Attach Quotation File</h3>
              </div>
              <div className="logout-modal-body">
                <div className="form-group">
                  <label>Select File</label>
                  <input type="file" ref={fileInputRefs.quotationAttachment} accept=".pdf,.jpg,.png,.xlsx" onChange={(e) => handleFileChange('quotationAttachment', e.target.files[0])} />
                  {quotationAttachment && <small>{quotationAttachment.name} selected</small>}
                </div>
              </div>
              <div className="logout-modal-footer">
                <button className="logout-modal-cancel" onClick={() => setShowQuotationFileModal(false)}>Cancel</button>
                <button className="logout-modal-confirm" onClick={handleUploadQuotationAttachment}>Upload</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderRfqTracker = () => {
    const filtered = filteredData(rfqData, ['supplierList', 'customerList']);
    const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
    const pageData = paginate(filtered, rfqPage);

    return (
      <div>
        <div className="section-header">
          <h2 className="section-title">Request for Quotation Tracker</h2>
        </div>

        <div className="form-container">
          <div className="form-grid">
            <div className="form-group">
              <label>Supplier List <span className="required-star">*</span></label>
              <select
                value={rfqForm.supplierList}
                onChange={(e) => setRfqForm({ ...rfqForm, supplierList: e.target.value })}
              >
                <option value="">Select Supplier</option>
                {suppliers.map(s => (
                  <option key={s.id} value={s.companyName}>{s.companyName}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Customer List <span className="required-star">*</span></label>
              <select
                value={rfqForm.customerList}
                onChange={(e) => setRfqForm({ ...rfqForm, customerList: e.target.value })}
              >
                <option value="">Select Customer</option>
                {approvedCustomers.map(c => (
                  <option key={c.id} value={c.name}>{c.name}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>No of RFQ requested <span className="required-star">*</span></label>
              <input type="number" value={rfqFetchedData.requested} disabled className="auto-field" />
              <small>Auto-filled from system</small>
            </div>
            <div className="form-group">
              <label>Requested Amount (₹)</label>
              <input type="number" value={rfqForm.requestedAmount} onChange={e => setRfqForm({ ...rfqForm, requestedAmount: parseFloat(e.target.value) || 0 })} />
            </div>

            <div className="form-group">
              <label>No of RFQ Quoted</label>
              <input type="number" value={rfqFetchedData.quoted} disabled className="auto-field" />
            </div>
            <div className="form-group">
              <label>Quoted Amount (₹)</label>
              <input type="number" value={rfqForm.quotedAmount} onChange={e => setRfqForm({ ...rfqForm, quotedAmount: parseFloat(e.target.value) || 0 })} />
            </div>

            <div className="form-group">
              <label>No of RFQ Pending</label>
              <input type="number" value={rfqFetchedData.pending} disabled className="auto-field" />
            </div>
            <div className="form-group">
              <label>Pending Amount (₹)</label>
              <input type="number" value={rfqForm.pendingAmount} onChange={e => setRfqForm({ ...rfqForm, pendingAmount: parseFloat(e.target.value) || 0 })} />
            </div>

            <div className="form-group">
              <label>No of RFQ Regrated</label>
              <input type="number" value={rfqFetchedData.regrated} disabled className="auto-field" />
            </div>
            <div className="form-group">
              <label>Regrated Amount (₹)</label>
              <input type="number" value={rfqForm.regratedAmount} onChange={e => setRfqForm({ ...rfqForm, regratedAmount: parseFloat(e.target.value) || 0 })} />
            </div>

            <div className="form-group">
              <label>No of RFQ Loss</label>
              <input type="number" value={rfqFetchedData.loss} disabled className="auto-field" />
            </div>
            <div className="form-group">
              <label>Loss Amount (₹)</label>
              <input type="number" value={rfqForm.lossAmount} onChange={e => setRfqForm({ ...rfqForm, lossAmount: parseFloat(e.target.value) || 0 })} />
            </div>
          </div>

          <div className="form-actions">
            <button className="btn-cancel" onClick={() => {
              setRfqForm({
                supplierList: '', customerList: '',
                noOfRfqRequested: 0, noOfRfqQuoted: 0, noOfRfqPending: 0, noOfRfqRegrated: 0, noOfRfqLoss: 0,
                requestedAmount: 0, quotedAmount: 0, pendingAmount: 0, regratedAmount: 0, lossAmount: 0
              });
              setRfqSubmitAttempted(false);
              setRfqFetchedData({ requested: 0, quoted: 0, pending: 0, regrated: 0, loss: 0, requestedAmount: 0, quotedAmount: 0, pendingAmount: 0, regratedAmount: 0, lossAmount: 0 });
            }}>Cancel</button>
            <button className="btn-submit" onClick={handleAddRFQ}>Submit</button>
          </div>
        </div>

        {rfqData.length > 0 && (
          <div className="list-section">
            <h3>RFQ Records</h3>
            <div className="data-table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Supplier</th><th>Customer</th><th>Requested</th><th>Req. Amount</th>
                    <th>Quoted</th><th>Quoted Amount</th><th>Pending</th><th>Pending Amount</th>
                    <th>Regrated</th><th>Regrated Amount</th><th>Loss</th><th>Loss Amount</th><th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pageData.map(rfq => (
                    <tr key={rfq.id}>
                      <td>{formatValue(rfq.supplierList)}</td>
                      <td>{formatValue(rfq.customerList)}</td>
                      <td>{rfq.noOfRfqRequested}</td>
                      <td>{rfq.requestedAmount}</td>
                      <td>{rfq.noOfRfqQuoted}</td>
                      <td>{rfq.quotedAmount}</td>
                      <td>{rfq.noOfRfqPending}</td>
                      <td>{rfq.pendingAmount}</td>
                      <td>{rfq.noOfRfqRegrated}</td>
                      <td>{rfq.regratedAmount}</td>
                      <td>{rfq.noOfRfqLoss}</td>
                      <td>{rfq.lossAmount}</td>
                      <td className="action-buttons">
                        <ActionIconButton icon={<Eye size={16} />} label="View" onClick={() => alert(JSON.stringify(rfq, null, 2))} colorClass="view" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <CustomPagination currentPage={rfqPage} totalPages={totalPages} onPageChange={setRfqPage} totalItems={filtered.length} />
          </div>
        )}
      </div>
    );
  };

  const renderTechnicalFeasibility = () => {
    const filtered = filteredData(techFeasibility, ['model', 'partNumber', 'customer']);
    const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
    const pageData = paginate(filtered, techPage);
    return (
      <div>
        <div className="section-header">
          <h2 className="section-title">Technical Feasibility Review Report</h2>
        </div>

        <div className="form-container">
          <div className="form-grid">
            <div className="form-group"><label>Date <span className="required-star">*</span></label><input type="date" value={techForm.date} onChange={(e) => setTechForm({ ...techForm, date: e.target.value })} /></div>
            <div className="form-group"><label>Model <span className="required-star">*</span></label><input type="text" value={techForm.model} onChange={(e) => setTechForm({ ...techForm, model: e.target.value })} /></div>
            <div className="form-group"><label>Part number <span className="required-star">*</span></label><input type="text" value={techForm.partNumber} onChange={(e) => setTechForm({ ...techForm, partNumber: e.target.value })} /></div>
            <div className="form-group"><label>Customer <span className="required-star">*</span></label><select value={techForm.customer} onChange={(e) => setTechForm({ ...techForm, customer: e.target.value })}><option value="">Select Customer</option>{approvedCustomers.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}</select></div>
            <div className="form-group"><label>Ass part name</label><input type="text" value={techForm.assPartName} onChange={(e) => setTechForm({ ...techForm, assPartName: e.target.value })} /></div>
            <div className="form-group"><label>Quantity required <span className="required-star">*</span></label><input type="number" value={techForm.quantityRequired} onChange={(e) => setTechForm({ ...techForm, quantityRequired: parseInt(e.target.value) || 0 })} /></div>
            <div className="form-group"><label>Quantity Required per mm</label><input type="number" value={techForm.quantityRequiredPerMm} onChange={(e) => setTechForm({ ...techForm, quantityRequiredPerMm: parseInt(e.target.value) || 0 })} /></div>
            <div className="form-group"><label>Number of Cavities Planned</label><input type="number" value={techForm.npOfCavityPlanned} onChange={(e) => setTechForm({ ...techForm, npOfCavityPlanned: parseInt(e.target.value) || 0 })} /></div>
            <div className="form-group"><label>Tool Designed by <span className="required-star">*</span></label><input type="text" value={techForm.toolDesignedBy} onChange={(e) => setTechForm({ ...techForm, toolDesignedBy: e.target.value })} /></div>
            <div className="form-group"><label>ISO review</label><input type="text" value={techForm.isoReview} onChange={(e) => setTechForm({ ...techForm, isoReview: e.target.value })} /></div>
            <div className="form-group"><label>Revision Number</label><input type="number" value={techForm.revisionNumber} onChange={(e) => setTechForm({ ...techForm, revisionNumber: parseInt(e.target.value) || 0 })} /></div>
          </div>

          <h4>Dimensions <span className="required-star">*</span></h4>
          <div className="dimensions-table-container">
            <table className="dimensions-table">
              <thead>
                <tr>
                  <th>S.No</th><th>Dimension</th><th>Special Char</th><th>Lower Tolerance</th><th>Upper Tolerance</th><th>Manufacturing Feasibility</th><th>Method of Inspection</th><th>Action</th>
                </tr>
              </thead>
              <tbody>
                {techForm.dimensions.map((dim, idx) => (
                  <tr key={dim.id}>
                    <td>{dim.sno}</td>
                    <td><input type="text" value={dim.dimension} onChange={(e) => updateDimension(idx, 'dimension', e.target.value)} /></td>
                    <td><select value={dim.specialChar} onChange={(e) => updateDimension(idx, 'specialChar', e.target.value)}><option>No</option><option>Yes</option></select></td>
                    <td><input type="text" value={dim.lowerTolerance} onChange={(e) => updateDimension(idx, 'lowerTolerance', e.target.value)} /></td>
                    <td><input type="text" value={dim.upperTolerance} onChange={(e) => updateDimension(idx, 'upperTolerance', e.target.value)} /></td>
                    <td><input type="text" value={dim.manufacturingFeasibility} onChange={(e) => updateDimension(idx, 'manufacturingFeasibility', e.target.value)} /></td>
                    <td><input type="text" value={dim.methodOfInspection} onChange={(e) => updateDimension(idx, 'methodOfInspection', e.target.value)} /></td>
                    <td><DeleteRowButton onClick={() => deleteDimension(idx)} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button className="add-field-btn" onClick={addDimension}><PlusCircle size={14} /> Add Row</button>

          <div className="form-grid" style={{ marginTop: '20px' }}>
            <div className="form-group full-width"><label>Rahul industries commands</label><textarea rows="2" value={techForm.rahulCommands} onChange={(e) => setTechForm({ ...techForm, rahulCommands: e.target.value })} /></div>
            <div className="form-group full-width"><label>Customer commands</label><textarea rows="2" value={techForm.customerCommands} onChange={(e) => setTechForm({ ...techForm, customerCommands: e.target.value })} /></div>
            <div className="form-group"><label>Common agreement date <span className="required-star">*</span></label><input type="date" value={techForm.commonAgreementDate} onChange={(e) => setTechForm({ ...techForm, commonAgreementDate: e.target.value })} /></div>
            <div className="form-group full-width">
              <label>Feedback Attachment</label>
              <input type="file" ref={fileInputRefs.techFeedback} accept="image/*,.pdf" onChange={(e) => handleFileChange('techFeedback', e.target.files[0])} />
              {techForm.feedbackAttachmentPreview && (
                <div className="drawing-preview">
                  <div className="preview-image">
                    <img src={techForm.feedbackAttachmentPreview} alt="Feedback" style={{ width: '80px', height: '80px' }} />
                    <button className="remove-preview" onClick={() => removePreview('techFeedback')}>×</button>
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="form-actions"><button className="btn-submit" onClick={handleAddTechFeasibility}>Submit Report</button></div>
        </div>

        {techFeasibility.length > 0 && (
          <div className="list-section">
            <h3>Technical Feasibility Reports</h3>
            <div className="data-table-container">
              <table className="data-table">
                <thead>
                  <tr><th>Date</th><th>Model</th><th>Part Number</th><th>Customer</th><th>Quantity</th><th>Revision</th><th>Feedback</th><th>Actions</th></tr>
                </thead>
                <tbody>
                  {pageData.map(tech => (
                    <tr key={tech.id}>
                      <td>{tech.date}</td>
                      <td className="highlight">{tech.model}</td>
                      <td>{tech.partNumber}</td>
                      <td>{tech.customer}</td>
                      <td>{tech.quantityRequired}</td>
                      <td>{tech.revisionNumber}</td>
                      <td>{tech.feedbackAttachment ? <a href={tech.feedbackAttachmentPreview} download="feedback">Download</a> : 'None'}</td>
                      <td className="action-buttons">
                        <ActionIconButton icon={<Eye size={16} />} label="View" onClick={() => alert(JSON.stringify(tech, null, 2))} colorClass="view" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <CustomPagination currentPage={techPage} totalPages={totalPages} onPageChange={setTechPage} totalItems={filtered.length} />
          </div>
        )}
      </div>
    );
  };

  const renderBOM = () => {
    const filteredEntries = bomEntries;
    const totalPages = Math.ceil(filteredEntries.length / PAGE_SIZE);
    const pageEntries = paginate(filteredEntries, bomPage);

    // Get child products for selected product
    const selectedProductObj = products.find(p => p.name === bomForm.selectedProduct);
    const childOptions = selectedProductObj?.childProducts || [];
    const selectedChildObj = childOptions.find(c => c.id === parseInt(bomForm.selectedChildProduct));
    const rawMaterialOptions = selectedChildObj?.rawMaterials || [];

    return (
      <div>
        <div className="section-header" style={{ display: 'flex', flexDirection: 'column', gap: '12px', alignItems: 'stretch' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <h2 className="section-title" style={{ margin: 0 }}>Bill of Materials (BOM)</h2>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
              <button
                type="button"
                className="btn-submit"
                onClick={() => setShowCreateProduct(true)}
                style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', width: 'auto' }}
              >
                <PlusCircle size={16} /> Add Product
              </button>
              <button
                type="button"
                className="btn-add"
                onClick={() => setShowRootSheetModal(true)}
                style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', width: 'auto' }}
              >
                <FileText size={16} /> Root Sheet
              </button>
            </div>
          </div>
          <h3 style={{ margin: 0, fontSize: '15px', fontWeight: 600, color: '#475569' }}>Add child product as a product before creating BOM</h3>
        </div>

        <ProductModal
          isOpen={showCreateProduct}
          title="Add Product"
          submitLabel="Add Product"
          onClose={() => { setShowCreateProduct(false); setProductSubmitAttempted(false); }}
          onSubmit={handleCreateProduct}
          productForm={productForm}
          setProductForm={setProductForm}
          errors={{
            name: getProductError('name'),
            code: getProductError('code'),
            category: getProductError('category'),
            cost: getProductError('cost')
          }}
          categories={categories}
          rawMaterials={rawMaterials}
          rawMaterialAssignment={rawMaterialAssignment}
          setRawMaterialAssignment={setRawMaterialAssignment}
          extraFields={productExtraFields}
          onAddExtraField={() => addExtraField('product')}
          onUpdateExtraField={(index, field, value) => updateExtraField('product', index, field, value)}
          onRemoveExtraField={(index) => removeExtraField('product', index)}
          onFileChange={handleFileChange}
          onCalculateTotalDimension={calculateTotalDimension}
        />

        {showRootSheetModal && (
          <div className="modal-overlay" onClick={() => setShowRootSheetModal(false)}>
            <div className="modal-content" style={{ maxWidth: '960px' }} onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Root Sheet</h2>
                <button className="modal-close" type="button" onClick={() => setShowRootSheetModal(false)}>×</button>
              </div>
              <div className="modal-body">
                <RootSheet variant="modal" />
              </div>
            </div>
          </div>
        )}


        <div className="form-container">
          <div className="form-grid">
            <div className="form-group">
              <label>Select Customer <span className="optional-text">(Optional)</span></label>
              <select value={bomForm.selectedCustomer} onChange={(e) => setBomForm({ ...bomForm, selectedCustomer: e.target.value, selectedProduct: '', selectedChildProduct: '', selectedRawMaterial: '' })}>
                <option value="">Select Customer</option>
                {approvedCustomers.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Select Product <span className="optional-text">(Optional)</span></label>
              <select value={bomForm.selectedProduct} onChange={(e) => setBomForm({ ...bomForm, selectedProduct: e.target.value, selectedChildProduct: '', selectedRawMaterial: '' })}>
                <option value="">Select Product</option>
                {products.map(p => <option key={p.id} value={p.name}>{p.name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Select Child Product <span className="optional-text">(Optional)</span></label>
              <select value={bomForm.selectedChildProduct} onChange={(e) => setBomForm({ ...bomForm, selectedChildProduct: e.target.value, selectedRawMaterial: '' })}>
                <option value="">Select Child Product</option>
                {childOptions.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Select Raw Material <span className="optional-text">(Optional)</span></label>
              <select value={bomForm.selectedRawMaterial} onChange={(e) => setBomForm({ ...bomForm, selectedRawMaterial: e.target.value })}>
                <option value="">Select Raw Material</option>
                {rawMaterialOptions.map(rm => <option key={rm.id} value={rm.id}>{rm.name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Product Quantity</label>
              <input type="number" value={bomForm.quantity} onChange={(e) => setBomForm({ ...bomForm, quantity: parseInt(e.target.value) || 1 })} />
            </div>
            <div className="form-group">
              <label>Raw Material Quantity</label>
              <input type="number" value={bomForm.rawMaterialQty} onChange={(e) => setBomForm({ ...bomForm, rawMaterialQty: parseInt(e.target.value) || 1 })} />
            </div>
            <div className="form-group">
              <label>Length (mm)</label>
              <input type="text" value={bomForm.length} onChange={(e) => setBomForm({ ...bomForm, length: e.target.value })} />
            </div>
            <div className="form-group">
              <label>Breadth (mm)</label>
              <input type="text" value={bomForm.breadth} onChange={(e) => setBomForm({ ...bomForm, breadth: e.target.value })} />
            </div>
            <div className="form-group">
              <label>Height (mm)</label>
              <input type="text" value={bomForm.breadth} onChange={(e) => setBomForm({ ...bomForm, height: e.target.value })} />
            </div>
            <div className="form-group">
              <label>Thickness (mm)</label>
              <input type="text" value={bomForm.breadth} onChange={(e) => setBomForm({ ...bomForm, Thickness: e.target.value })} />
            </div>
            <div className="form-group">
              <label>Dimension (sq mm)</label>
              <input type="text" value={bomForm.dimension} onChange={(e) => setBomForm({ ...bomForm, dimension: e.target.value })} />
            </div>
            <div className="form-group">
              <label>Add Formula to Calculate RM</label>
              <input type="text" value={bomForm.dimension} onChange={(e) => setBomForm({ ...bomForm, dimension: e.target.value })} />
            </div>
          </div>
          <div className="form-actions">
            <button className="btn-submit" onClick={handleGenerateBOM}>Add to BOM</button>
          </div>
        </div>

        {/* BOM Entries List */}
        <div className="list-section">
          <h3>BOM Entries</h3>
          {bomEntries.length === 0 ? (
            <div className="empty-state"><Package size={48} color="#CBD5E1" /><p>No BOM entries added yet</p></div>
          ) : (
            <>
              <div className="data-table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Customer</th><th>Product</th><th>Child Product</th><th>Raw Material</th>
                      <th>Qty (Prod)</th><th>RM Qty</th><th>Length</th><th>Breadth</th><th>Dimension</th><th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageEntries.map(entry => (
                      <tr key={entry.id}>
                        <td>{entry.customer}</td>
                        <td>{entry.product}</td>
                        <td>{entry.childProduct}</td>
                        <td>{entry.rawMaterial}</td>
                        <td>{entry.quantity}</td>
                        <td>{entry.rawMaterialQty}</td>
                        <td>{entry.length}</td>
                        <td>{entry.breadth}</td>
                        <td>{entry.dimension}</td>
                        <td>{new Date(entry.createdAt).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <CustomPagination currentPage={bomPage} totalPages={totalPages} onPageChange={setBomPage} totalItems={filteredEntries.length} />
            </>
          )}
        </div>
      </div>
    );
  };

  const renderSamples = () => {
    const filtered = filteredData(samplesData, ['selectDrawing', 'selectQuotation']);
    const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
    const pageData = paginate(filtered, 1);
    return (
      <div>
        <div className="section-header">
          <h2 className="section-title">Samples</h2>
        </div>

        <div className="form-container">
          <div className="form-grid">
            <div className="form-group">
              <label>Select Product<span className="required-star">*</span></label>
              <select value={sampleForm.selectDrawing} onChange={(e) => setSampleForm({ ...sampleForm, selectDrawing: e.target.value })}>
                <option value="">Select Product</option>
                {drawings.map(d => <option key={d.id}>Drawing {d.id} - {d.leads}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Select Child Product<span className="required-star">*</span></label>
              <select value={sampleForm.selectDrawing} onChange={(e) => setSampleForm({ ...sampleForm, selectDrawing: e.target.value })}>
                <option value="">Select Child Product</option>
                {drawings.map(d => <option key={d.id}>Drawing {d.id} - {d.leads}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Select Drawing <span className="required-star">*</span></label>
              <select value={sampleForm.selectDrawing} onChange={(e) => setSampleForm({ ...sampleForm, selectDrawing: e.target.value })}>
                <option value="">Select Drawing</option>
                {drawings.map(d => <option key={d.id}>Drawing {d.id} - {d.leads}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Select Quotation <span className="required-star">*</span></label>
              <select value={sampleForm.selectQuotation} onChange={(e) => setSampleForm({ ...sampleForm, selectQuotation: e.target.value })}>
                <option value="">Select Quotation</option>
                {quotations.map(q => <option key={q.id}>{q.partNumber} - {q.partName}</option>)}
              </select>
            </div>
          </div>

          <div className="form-group full-width" style={{ marginBottom: '16px' }}>
            <label>Feasibility Remark</label>
            <textarea rows="3" value={sampleForm.feasibilityRemark} onChange={(e) => setSampleForm({ ...sampleForm, feasibilityRemark: e.target.value })} />
          </div>

          {/* <div className="form-group">
            <label>Upload sample image/pdf</label>
            <input type="file" ref={fileInputRefs.sample} accept="image/*,.pdf" onChange={(e) => handleFileChange('sample', e.target.files[0])} />
            {samplePreview && (
              <div className="drawing-preview">
                <div className="preview-image">
                  <img src={samplePreview} alt="Sample Preview" />
                  <button className="remove-preview" onClick={() => removePreview('sample')}>×</button>
                </div>
              </div>
            )}
          </div> */}
          <FileEntryUploader />

          <div className="form-actions">
            <button className="btn-submit" onClick={handleAddSample}>Submit Sample</button>
          </div>
        </div>

        {samplesData.length > 0 && (
          <div className="list-section">
            <h3>Submitted Samples</h3>
            <div className="data-table-container">
              <table className="data-table">
                <thead><tr><th>Drawing</th><th>Quotation</th><th>Feasibility Remark</th><th>Date</th><th>Actions</th></tr></thead>
                <tbody>
                  {pageData.map(sample => (
                    <tr key={sample.id}>
                      <td className="highlight">{sample.selectDrawing}</td>
                      <td>{sample.selectQuotation}</td>
                      <td>{sample.feasibilityRemark || 'N/A'}</td>
                      <td>{new Date(sample.createdAt).toLocaleDateString()}</td>
                      <td className="action-buttons">
                        <ActionIconButton icon={<Eye size={16} />} label="View" onClick={() => alert(JSON.stringify(sample, null, 2))} colorClass="view" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <CustomPagination currentPage={1} totalPages={totalPages} onPageChange={() => { }} totalItems={filtered.length} />
          </div>
        )}
      </div>
    );
  };

  const renderPPAP = () => {
    const filtered = filteredData(ppapData, ['customer', 'product', 'childProduct']);
    const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
    const pageData = paginate(filtered, ppapPage);

    // Get products for PPAP
    const ppapProductOptions = products;
    const ppapChildOptions = ppapForm.productId ? products.find(p => p.id === parseInt(ppapForm.productId))?.childProducts || [] : [];
    const selectedPpapProduct = ppapProductOptions.find(p => p.id === parseInt(ppapForm.productId));
    const selectedPpapChild = selectedPpapProduct?.childProducts.find(c => c.id === parseInt(ppapForm.childProductId));
    const ppapRawMaterialOptions = selectedPpapChild?.rawMaterials || [];

    return (
      <div>
        <div className="section-header">
          <h2 className="section-title">PPAP (Production Part Approval Process)</h2>
        </div>

        <div className="form-container">
          <div className="form-grid">
            <div className="form-group">
              <label>Customer <span className="required-star">*</span></label>
              <select value={ppapForm.customer} onChange={(e) => setPpapForm({ ...ppapForm, customer: e.target.value })}>
                <option value="">Select Customer</option>
                {approvedCustomers.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Product <span className="required-star">*</span></label>
              <select value={ppapForm.productId} onChange={(e) => setPpapForm({ ...ppapForm, productId: e.target.value, childProductId: '', rawMaterialId: '' })}>
                <option value="">Select Product</option>
                {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Child Product</label>
              <select value={ppapForm.childProductId} onChange={(e) => setPpapForm({ ...ppapForm, childProductId: e.target.value, rawMaterialId: '' })}>
                <option value="">Select Child Product</option>
                {ppapChildOptions.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Raw Material</label>
              <select value={ppapForm.rawMaterialId} onChange={(e) => setPpapForm({ ...ppapForm, rawMaterialId: e.target.value })}>
                <option value="">Select Raw Material</option>
                {ppapRawMaterialOptions.map(rm => <option key={rm.id} value={rm.id}>{rm.name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Gauges</label>
              <input type="text" value={ppapForm.gauges} onChange={(e) => setPpapForm({ ...ppapForm, gauges: e.target.value })} placeholder="Enter gauges details" />
            </div>
            <div className="form-group">
              <label>Tools</label>
              <input type="text" value={ppapForm.tools} onChange={(e) => setPpapForm({ ...ppapForm, tools: e.target.value })} placeholder="Enter tools details" />
            </div>
            <div className="form-group">
              <label>Fixtures</label>
              <input type="text" value={ppapForm.fixtures} onChange={(e) => setPpapForm({ ...ppapForm, fixtures: e.target.value })} placeholder="Enter fixtures details" />
            </div>
            {/* <div className="form-group full-width">
              <label>Attachment</label>
              <input type="file" ref={fileInputRefs.ppapAttachment} accept=".pdf,.jpg,.png" onChange={(e) => handleFileChange('ppapAttachment', e.target.files[0])} />
              {ppapForm.attachment && <small>File attached</small>}
            </div> */}
          </div>
            {/* <h2>Select Child Product to view the Production plan</h2> */}
            < FileEntryUploader />
          <div className="form-actions">
            <button className="btn-submit" onClick={handleAddPPAP}>Submit PPAP</button>
          </div>
        </div>

        {ppapData.length > 0 && (
          <div className="list-section">
            <h3>PPAP Submissions</h3>
            <div className="data-table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Customer</th><th>Product</th><th>Child Product</th><th>Raw Material</th>
                    <th>Gauges</th><th>Tools</th><th>Fixtures</th><th>Attachment</th><th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {pageData.map(ppap => (
                    <tr key={ppap.id}>
                      <td className="highlight">{ppap.customer}</td>
                      <td>{ppap.product}</td>
                      <td>{ppap.childProduct}</td>
                      <td>{ppap.rawMaterial}</td>
                      <td>{ppap.gauges}</td>
                      <td>{ppap.tools}</td>
                      <td>{ppap.fixtures}</td>
                      <td>{ppap.attachment ? <a href={ppap.attachment} download="ppap_file">Download</a> : 'None'}</td>
                      <td>{new Date(ppap.createdAt).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <CustomPagination currentPage={ppapPage} totalPages={totalPages} onPageChange={setPpapPage} totalItems={filtered.length} />
          </div>
        )}
      </div>
    );
  };

  const renderListApprovedDrawing = () => {
    const groupedByCustomer = approvedDrawings.reduce((acc, drawing) => {
      if (!acc[drawing.customerName]) acc[drawing.customerName] = [];
      acc[drawing.customerName].push(drawing);
      return acc;
    }, {});

    return (
      <div>
        <div className="section-header">
          <h2 className="section-title">List of Approved Drawings</h2>
        </div>

        {Object.entries(groupedByCustomer).map(([customerName, drawingsList]) => (
          <div key={customerName} className="list-section" style={{ marginBottom: '32px' }}>
            <h3 style={{ color: '#4F46E5', fontSize: '20px', marginBottom: '20px' }}>{customerName}</h3>
            {drawingsList.map((drawing, idx) => (
              <div key={drawing.id} className="form-container" style={{ marginBottom: '20px', background: '#F8FAFC' }}>
                <h4 style={{ marginBottom: '16px', fontSize: '16px', fontWeight: 600 }}>Drawing {idx + 1}: {drawing.drawingName}</h4>
                <div className="form-grid" style={{ marginBottom: '16px' }}>
                  <div className="form-group">
                    <label>Attached Quantities</label>
                    <div style={{ border: '1px solid #e2e8f0', borderRadius: '8px', padding: '12px', background: '#FEF3C7', minHeight: '60px' }}>
                      {drawing.attachedQuantities?.length > 0 ? drawing.attachedQuantities.map((item, i) => (
                        <span key={i} style={{ display: 'inline-block', background: '#FDE68A', padding: '4px 8px', borderRadius: '4px', margin: '4px', fontSize: '12px' }}>{item} (Pending)</span>
                      )) : <span className="optional-text">No attached quantities</span>}
                      <button className="add-field-btn" style={{ marginLeft: '8px', padding: '4px 8px', fontSize: '11px' }}><PlusCircle size={12} /> Attach Quantity</button>
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Approved Quantities</label>
                    <div style={{ border: '1px solid #e2e8f0', borderRadius: '8px', padding: '12px', background: '#D1FAE5', minHeight: '60px' }}>
                      {drawing.approvedQuantities?.length > 0 ? drawing.approvedQuantities.map((item, i) => (
                        <span key={i} style={{ display: 'inline-block', background: '#A7F3D0', padding: '4px 8px', borderRadius: '4px', margin: '4px', fontSize: '12px' }}>{item} (Approved)</span>
                      )) : <span className="optional-text">No approved quantities</span>}
                    </div>
                  </div>
                </div>
                <div className="form-actions" style={{ justifyContent: 'flex-start' }}>
                  <a
                    href={drawing.fileUrl}
                    download
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '8px 20px',
                      background: 'linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: 500,
                      textDecoration: 'none',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.transform = 'translateY(-1px)';
                      e.target.style.boxShadow = '0 4px 12px rgba(79, 70, 229, 0.3)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = 'none';
                    }}
                  >
                    <Download size={16} /> Download Drawing
                  </a>
                </div>
              </div>
            ))}
          </div>
        ))}

        {approvedDrawings.length === 0 && (
          <div className="empty-state"><FileText size={48} color="#CBD5E1" /><p>No approved drawings available</p></div>
        )}
      </div>
    );
  };

  const renderListApprovedCustomer = () => {
    const filtered = filteredData(approvedCustomers, ['name']);
    const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
    const pageData = paginate(filtered, approvedDrawingPage);

    const handleSelectProduct = (customerId, productId, productName) => {
      setSelectedProducts(prev => ({
        ...prev,
        [customerId]: {
          ...(prev[customerId] || {}),
          [productId]: !(prev[customerId]?.[productId] || false)
        }
      }));
      const isSelected = !(selectedProducts[customerId]?.[productId] || false);
      alert(`${productName} ${isSelected ? 'added to' : 'removed from'} selection`);
    };

    const handleAddToGroup = (customerName) => {
      setSelectedCustomersForGroup(prev =>
        prev.includes(customerName)
          ? prev.filter(c => c !== customerName)
          : [...prev, customerName]
      );
    };

    const handleCreateGroup = () => {
      if (!groupName.trim()) {
        alert("Please enter a group name");
        return;
      }
      if (selectedCustomersForGroup.length === 0) {
        alert("Please select at least one customer");
        return;
      }
      const newGroup = {
        id: Date.now(),
        name: groupName,
        customers: [...selectedCustomersForGroup],
        createdAt: new Date().toISOString()
      };
      setCustomerGroups([...customerGroups, newGroup]);
      setGroupName("");
      setSelectedCustomersForGroup([]);
      setShowGroupModal(false);
      alert(`Group "${groupName}" created with ${selectedCustomersForGroup.length} customers`);
    };

    const handleEditGroup = (group) => {
      setEditingGroup(group.id);
      setEditGroupName(group.name);
    };

    const handleSaveGroupEdit = (groupId) => {
      if (!editGroupName.trim()) {
        alert("Please enter a group name");
        return;
      }
      setCustomerGroups(groups => groups.map(g =>
        g.id === groupId ? { ...g, name: editGroupName } : g
      ));
      setEditingGroup(null);
      setEditGroupName("");
      alert("Group name updated successfully!");
    };

    const handleDeleteGroup = (groupId) => {
      if (window.confirm("Are you sure you want to delete this group?")) {
        setCustomerGroups(groups => groups.filter(g => g.id !== groupId));
        if (expandedGroup === groupId) setExpandedGroup(null);
        alert("Group deleted successfully!");
      }
    };

    const handleRemoveCustomerFromGroup = (groupId, customerName) => {
      if (window.confirm(`Remove ${customerName} from this group?`)) {
        setCustomerGroups(groups => groups.map(g =>
          g.id === groupId
            ? { ...g, customers: g.customers.filter(c => c !== customerName) }
            : g
        ));
        alert(`Customer ${customerName} removed from group`);
      }
    };

    return (
      <div>
        <div className="section-header">
          <h2 className="section-title">List of Approved Customers</h2>
          <button
            className="btn-submit"
            onClick={() => setShowGroupModal(true)}
            style={{ background: "linear-gradient(135deg, #10B981 0%, #059669 100%)" }}
          >
            <Users size={16} style={{ marginRight: '8px' }} />
            Create Group
          </button>
        </div>

        {approvedCustomers.length === 0 ? (
          <div className="empty-state"><Users size={48} color="#CBD5E1" /><p>No approved customers available</p></div>
        ) : (
          <>
            <div className="data-table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Select</th>
                    <th>Customer Name</th>
                    <th>Products</th>
                    <th>Selected Products</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pageData.map(customer => (
                    <tr key={customer.id}>
                      <td style={{ textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          checked={selectedCustomersForGroup.includes(customer.name)}
                          onChange={() => handleAddToGroup(customer.name)}
                          style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                        />
                      </td>
                      <td className="highlight">{customer.name}</td>
                      <td>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          {customer.products?.map(product => (
                            <label key={product.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                              <input
                                type="checkbox"
                                checked={selectedProducts[customer.id]?.[product.id] || false}
                                onChange={() => handleSelectProduct(customer.id, product.id, product.name)}
                                style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                              />
                              <span>{product.name}</span>
                            </label>
                          ))}
                        </div>
                      </td>
                      <td>
                        {selectedProducts[customer.id] && Object.keys(selectedProducts[customer.id]).filter(key => selectedProducts[customer.id][key]).length > 0 ? (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                            {customer.products?.filter(p => selectedProducts[customer.id]?.[p.id]).map(p => (
                              <span key={p.id} style={{
                                background: '#D1FAE5',
                                padding: '2px 8px',
                                borderRadius: '12px',
                                fontSize: '11px',
                                color: '#065F46'
                              }}>
                                {p.name}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="optional-text">No products selected</span>
                        )}
                      </td>
                      <td className="action-buttons">
                        <ActionIconButton
                          icon={<Eye size={16} />}
                          label="View Selection"
                          onClick={() => alert(`Customer: ${customer.name}\nSelected Products: ${customer.products?.filter(p => selectedProducts[customer.id]?.[p.id]).map(p => p.name).join(', ') || 'None'}`)}
                          colorClass="view"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <CustomPagination
              currentPage={approvedDrawingPage}
              totalPages={totalPages}
              onPageChange={setApprovedDrawingPage}
              totalItems={filtered.length}
            />
          </>
        )}

        {/* Customer Groups Section */}
        {customerGroups.length > 0 && (
          <div className="list-section">
            <h3>Customer Groups</h3>
            <div className="data-cards">
              {customerGroups.map(group => (
                <div key={group.id} className="data-card">
                  <div className="data-card-header">
                    {editingGroup === group.id ? (
                      <input
                        type="text"
                        value={editGroupName}
                        onChange={(e) => setEditGroupName(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleSaveGroupEdit(group.id)}
                        style={{
                          fontSize: '16px',
                          fontWeight: 'bold',
                          padding: '4px 8px',
                          border: '1px solid #4F46E5',
                          borderRadius: '6px',
                          width: 'auto'
                        }}
                        autoFocus
                      />
                    ) : (
                      <strong
                        onClick={() => setExpandedGroup(expandedGroup === group.id ? null : group.id)}
                        style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}
                      >
                        {expandedGroup === group.id ? '📂' : '📁'} {group.name}
                        <span style={{ fontSize: '12px', color: '#6B7280' }}>({group.customers.length} customers)</span>
                      </strong>
                    )}
                    <div className="action-buttons">
                      {editingGroup === group.id ? (
                        <>
                          <ActionIconButton icon={<CheckCircle size={14} />} label="Save" onClick={() => handleSaveGroupEdit(group.id)} colorClass="edit" />
                          <ActionIconButton icon={<X size={14} />} label="Cancel" onClick={() => setEditingGroup(null)} colorClass="delete" />
                        </>
                      ) : (
                        <>
                          <ActionIconButton icon={<Edit size={14} />} label="Edit Group" onClick={() => handleEditGroup(group)} colorClass="edit" />
                          <ActionIconButton icon={<Trash2 size={14} />} label="Delete Group" onClick={() => handleDeleteGroup(group.id)} colorClass="delete" />
                        </>
                      )}
                    </div>
                  </div>
                  {expandedGroup === group.id && (
                    <div className="data-card-details" style={{ marginTop: '12px' }}>
                      {group.customers.length === 0 ? (
                        <p className="optional-text">No customers in this group</p>
                      ) : (
                        group.customers.map(customer => (
                          <div key={customer} style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '8px 0',
                            borderBottom: '1px solid #E5E7EB'
                          }}>
                            <span>👤 {customer}</span>
                            <ActionIconButton icon={<X size={12} />} label="Remove from group" onClick={() => handleRemoveCustomerFromGroup(group.id, customer)} colorClass="delete" />
                          </div>
                        ))
                      )}
                      <div style={{ marginTop: '12px' }}>
                        <small className="optional-text">Created: {new Date(group.createdAt).toLocaleDateString()}</small>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Create Group Modal */}
        {showGroupModal && (
          <div className="logout-modal-overlay" onClick={() => setShowGroupModal(false)}>
            <div className="logout-modal" style={{ maxWidth: '500px' }} onClick={(e) => e.stopPropagation()}>
              <div className="logout-modal-header" style={{ background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)' }}>
                <div className="logout-modal-icon">👥</div>
                <h3>Create Customer Group</h3>
              </div>
              <div className="logout-modal-body">
                <p>Selected Customers: <strong>{selectedCustomersForGroup.length}</strong></p>
                {selectedCustomersForGroup.length > 0 && (
                  <div style={{
                    background: '#F3F4F6',
                    padding: '8px',
                    borderRadius: '8px',
                    marginBottom: '16px',
                    fontSize: '12px'
                  }}>
                    {selectedCustomersForGroup.join(', ')}
                  </div>
                )}
                <div className="form-group" style={{ textAlign: 'left' }}>
                  <label>Group Name <span className="required-star">*</span></label>
                  <input
                    type="text"
                    value={groupName}
                    onChange={(e) => setGroupName(e.target.value)}
                    placeholder="e.g., Premium Customers, Domestic Clients"
                    style={{ width: '100%', boxSizing: 'border-box' }}
                    autoFocus
                  />
                </div>
              </div>
              <div className="logout-modal-footer">
                <button className="logout-modal-cancel" onClick={() => {
                  setShowGroupModal(false);
                  setGroupName("");
                  setSelectedCustomersForGroup([]);
                }}>
                  Cancel
                </button>
                <button className="logout-modal-confirm" onClick={handleCreateGroup} style={{ background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)' }}>
                  Create Group
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderCreateSupplier = () => {
    const filtered = filteredData(suppliers, ['companyName', 'pocName', 'username']);
    const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
    const pageData = paginate(filtered, supplierPage);

    const viewSupplierDocs = (supplier, type) => {
      if (type === 'general') setCurrentSupplierDocs(supplier.documents || []);
      else setCurrentSupplierDocs(supplier.qualityDocs || []);
      setShowSupplierDocsModal(true);
    };

    return (
      <div>
        <div className="section-header">
          <h2 className="section-title">Create Supplier</h2>
        </div>

        <div className="form-container">
          <div className="form-grid">
            <div className="form-group"><label>POC Name <span className="required-star">*</span></label><input type="text" value={supplierForm.pocName} onChange={e => setSupplierForm({ ...supplierForm, pocName: e.target.value })} className={getSuppErr('pocName') ? 'error' : ''} />{getSuppErr('pocName') && <span className="error-text">{getSuppErr('pocName')}</span>}</div>
            <div className="form-group"><label>Company Name <span className="required-star">*</span></label><input type="text" value={supplierForm.companyName} onChange={e => setSupplierForm({ ...supplierForm, companyName: e.target.value })} className={getSuppErr('companyName') ? 'error' : ''} />{getSuppErr('companyName') && <span className="error-text">{getSuppErr('companyName')}</span>}</div>
            <div className="form-group"><label>Username</label><input type="text" value={supplierForm.companyName.toLowerCase().replace(/\s/g, '') + Date.now()} disabled className="auto-field" /><small>Auto-generated</small></div>
            <div className="form-group"><label>Password <span className="optional-text">(Optional)</span></label><input type="password" value={supplierForm.password} onChange={e => setSupplierForm({ ...supplierForm, password: e.target.value })} /></div>
            <div className="form-group"><label>PAN <span className="required-star">*</span></label><input type="text" value={supplierForm.pan} onChange={e => setSupplierForm({ ...supplierForm, pan: e.target.value })} className={getSuppErr('pan') ? 'error' : ''} />{getSuppErr('pan') && <span className="error-text">{getSuppErr('pan')}</span>}</div>
            <div className="form-group"><label>Bank Details <span className="required-star">*</span></label><input type="text" value={supplierForm.bankDetails} onChange={e => setSupplierForm({ ...supplierForm, bankDetails: e.target.value })} className={getSuppErr('bankDetails') ? 'error' : ''} />{getSuppErr('bankDetails') && <span className="error-text">{getSuppErr('bankDetails')}</span>}</div>
            <div className="form-group"><label>GST <span className="required-star">*</span></label><input type="text" value={supplierForm.gst} onChange={e => setSupplierForm({ ...supplierForm, gst: e.target.value })} className={getSuppErr('gst') ? 'error' : ''} />{getSuppErr('gst') && <span className="error-text">{getSuppErr('gst')}</span>}</div>
            <div className="form-group"><label>Phone <span className="required-star">*</span></label><input type="tel" value={supplierForm.phone} onChange={e => setSupplierForm({ ...supplierForm, phone: e.target.value })} className={getSuppErr('phone') ? 'error' : ''} />{getSuppErr('phone') && <span className="error-text">{getSuppErr('phone')}</span>}</div>
            <div className="form-group"><label>Supplier Code</label><input type="text" value={supplierForm.supplierCode} disabled className="auto-field" /><small>Auto-generated</small></div>
          </div>

          <div className="form-group full-width" style={{ marginBottom: '16px' }}>
            <label>Address <span className="required-star">*</span></label>
            <textarea rows="3" value={supplierForm.address} onChange={e => setSupplierForm({ ...supplierForm, address: e.target.value })} className={getSuppErr('address') ? 'error' : ''} />
            {getSuppErr('address') && <span className="error-text">{getSuppErr('address')}</span>}
          </div>

          {/* General Documents upload */}
          <div style={{ margin: '16px 0', padding: '16px', background: '#F9FAFB', borderRadius: '8px' }}>
            <label style={{ fontWeight: 600, fontSize: '13px', color: '#475569' }}>General Documents (Optional)</label>
            <input type="file" ref={fileInputRefs.supplierDoc} accept=".pdf,.jpg,.png,.docx" onChange={(e) => handleFileChange('supplierDoc', e.target.files[0])} />
            {supplierForm.documents.length > 0 && (
              <div className="drawing-preview">
                {supplierForm.documents.map((doc, idx) => (
                  <div key={idx} className="preview-image" style={{ width: 'auto', padding: '4px 12px' }}>
                    <span>{doc.name}</span>
                    <button className="remove-preview" onClick={() => removePreview('supplierDoc', idx)}>×</button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Quality Documents upload (required) */}
          <div style={{ margin: '16px 0', padding: '16px', background: '#F9FAFB', borderRadius: '8px' }}>
            <label style={{ fontWeight: 600, fontSize: '13px', color: '#475569' }}>Quality Documents <span className="required-star">*</span></label>
            <input type="file" accept=".pdf,.jpg,.png,.docx" onChange={(e) => handleFileChange('supplierQualityDoc', e.target.files[0])} />
            {supplierForm.qualityDocs.length > 0 && (
              <div className="drawing-preview">
                {supplierForm.qualityDocs.map((doc, idx) => (
                  <div key={idx} className="preview-image" style={{ width: 'auto', padding: '4px 12px' }}>
                    <span>{doc.name}</span>
                    <button className="remove-preview" onClick={() => removePreview('supplierQualityDoc', idx)}>×</button>
                  </div>
                ))}
              </div>
            )}
            {getSuppErr('qualityDocs') && <span className="error-text">Quality documents are required</span>}
          </div>

          <div style={{ margin: '16px 0', padding: '16px', background: '#F9FAFB', borderRadius: '8px' }}>
            <label style={{ fontWeight: 600, fontSize: '13px', color: '#475569' }}>Add Extra Fields <span className="optional-text">(Optional)</span></label>
            {supplierExtraFields.map((field, index) => (
              <div key={index} style={{ display: 'flex', gap: '12px', marginTop: '10px', alignItems: 'center' }}>
                <input type="text" placeholder="Field name" value={field.name} onChange={e => updateExtraField('supplier', index, 'name', e.target.value)} style={{ flex: 1, padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '14px' }} />
                <input type="text" placeholder="Field value" value={field.value} onChange={e => updateExtraField('supplier', index, 'value', e.target.value)} style={{ flex: 1, padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '14px' }} />
                <button onClick={() => removeExtraField('supplier', index)} style={{ padding: '6px', background: '#FEE2E2', border: 'none', borderRadius: '6px', cursor: 'pointer', color: '#DC2626' }}><X size={16} /></button>
              </div>
            ))}
            <button className="add-field-btn" onClick={() => addExtraField('supplier')} style={{ marginTop: '10px' }}><PlusCircle size={14} /> Add Extra fields</button>
          </div>

          <div className="form-actions">
            <button className="btn-cancel" onClick={() => { setSupplierForm({ pocName: '', companyName: '', username: '', password: '', address: '', pan: '', bankDetails: '', gst: '', phone: '', supplierCode: 'SUP-' + Date.now(), documents: [], qualityDocs: [] }); setSupplierExtraFields([]); setSupplierSubmitAttempted(false); }}>Cancel</button>
            <button className="btn-submit" onClick={handleCreateSupplier}>Submit</button>
          </div>
        </div>

        <div className="list-section">
          <h3>List Suppliers ({filtered.length})</h3>
          {suppliers.length === 0 ? (
            <div className="empty-state"><p>No suppliers yet. Create a supplier to see it here.</p></div>
          ) : (
            <>
              <div className="data-table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>POC Name</th><th>Company Name</th><th>Username</th><th>Address</th><th>Supplier Code</th><th>General Docs</th><th>Quality Docs</th><th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageData.map(supplier => (
                      <tr key={supplier.id}>
                        <td>{formatValue(supplier.pocName)}</td>
                        <td className="highlight">{formatValue(supplier.companyName)}</td>
                        <td>{formatValue(supplier.username)}</td>
                        <td>{formatValue(supplier.address)}</td>
                        <td>{formatValue(supplier.supplierCode)}</td>
                        <td>
                          {supplier.documents && supplier.documents.length > 0 ? (
                            <button className="action-btn view" onClick={() => viewSupplierDocs(supplier, 'general')} style={{ width: 'auto', padding: '4px 12px' }}>
                              <Eye size={14} /> {supplier.documents.length}
                            </button>
                          ) : 'None'}
                        </td>
                        <td>
                          {supplier.qualityDocs && supplier.qualityDocs.length > 0 ? (
                            <button className="action-btn view" onClick={() => viewSupplierDocs(supplier, 'quality')} style={{ width: 'auto', padding: '4px 12px' }}>
                              <Eye size={14} /> {supplier.qualityDocs.length}
                            </button>
                          ) : 'None'}
                        </td>
                        <td className="action-buttons">
                          <ActionIconButton icon={<Eye size={16} />} label="View" onClick={() => alert(JSON.stringify(supplier, null, 2))} colorClass="view" />
                          <ActionIconButton icon={<Edit size={16} />} label="Edit" onClick={() => alert('Edit feature coming soon')} colorClass="edit" />
                          <ActionIconButton icon={<Trash2 size={16} />} label="Delete" onClick={() => { if (window.confirm('Delete?')) setSuppliers(suppliers.filter(s => s.id !== supplier.id)); }} colorClass="delete" />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <CustomPagination currentPage={supplierPage} totalPages={totalPages} onPageChange={setSupplierPage} totalItems={filtered.length} />
            </>
          )}
        </div>

        {/* Supplier Documents Modal */}
        {showSupplierDocsModal && (
          <div className="logout-modal-overlay" onClick={() => setShowSupplierDocsModal(false)}>
            <div className="logout-modal" style={{ maxWidth: '600px' }} onClick={(e) => e.stopPropagation()}>
              <div className="logout-modal-header" style={{ background: 'linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)' }}>
                <div className="logout-modal-icon">📄</div>
                <h3>Supplier Documents</h3>
              </div>
              <div className="logout-modal-body">
                {currentSupplierDocs.length === 0 ? (
                  <p>No documents uploaded.</p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {currentSupplierDocs.map((doc, idx) => (
                      <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e5e7eb', padding: '8px 0' }}>
                        <span>{doc.name}</span>
                        <a href={doc.data} download={doc.name} className="action-btn view" style={{ width: 'auto', padding: '4px 12px' }}>
                          Download
                        </a>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="logout-modal-footer">
                <button className="logout-modal-cancel" onClick={() => setShowSupplierDocsModal(false)}>Close</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderContent = () => {
    switch (activeTab) {
      case "add-marketing-data": return renderAddMarketingData();
      case "list-marketing-data": return renderListMarketingData();
      case "list-next-followup": return renderListNextFollowup();
      case "add-drawing": return renderAddDrawing();
      case "select-drawing-quotation": return renderSelectDrawingQuotation();
      case "quotation-history": return renderQuotationHistory();
      case "rfq-tracker": return renderRfqTracker();
      case "technical-feasibility": return renderTechnicalFeasibility();
      case "bom": return renderBOM();
      case "samples": return renderSamples();
      case "ppap": return renderPPAP();
      case "list-approved-drawing": return renderListApprovedDrawing();
      case "list-approved-customer": return renderListApprovedCustomer();
      case "create-supplier": return renderCreateSupplier();
      default: return renderAddMarketingData();
    }
  };

  return (
    <div className="ndp-dashboard-container">
      <div className="ndp-sidebar">
        <div className="ndp-logo-container">
          <div className="ndp-logo-icon">
            <img src="/rahulerp.jpg" alt="Logo" className="ndp-logo-image" onError={(e) => { e.target.src = 'https://via.placeholder.com/48x48?text=RAT'; }} />
          </div>
          <div>
            <h2 className="ndp-company-name">Ragul Auto Tech</h2>
            <p className="ndp-company-tagline">AI Powered ERP</p>
          </div>
        </div>
        <nav className="ndp-nav-menu">
          {tabItems.map((tab) => (
            <button key={tab.id} className={`ndp-nav-item ${activeTab === tab.id ? 'ndp-nav-item-active' : ''}`} onClick={() => setActiveTab(tab.id)}>
              <span className="ndp-nav-icon">{tab.icon}</span>
              <span>{tab.label}</span>
            </button>
          ))}
        </nav>
        <div className="ndp-logout-container">
          <button onClick={handleLogoutClick} className="ndp-logout-button"><LogOut size={18} /> Logout</button>
        </div>
      </div>

      <div className="ndp-main-content">
        <ScreenHeader
          title="New Product Development Dashboard"
          description={`Welcome, ${user?.role || 'NDP Manager'}`}
          timestamp={new Date().toLocaleString()}
          containerClassName="ndp-header"
          titleClassName="ndp-main-title"
          descriptionClassName="ndp-subtitle"
          actionsClassName="ndp-header-actions"
          timestampClassName="ndp-last-updated"
        />

        {cameFromMDDashboard && (
          <div className="ndp-back-button-wrapper">
            <button onClick={handleBackToMD} className="btn-edit" style={{ background: "linear-gradient(135deg, #4F46E5 0%, #6366F1 100%)" }}>
              <ArrowLeft size={18} /> Back to MD Dashboard
            </button>
          </div>
        )}

        <div className="ndp-scrollable-content">
          <div className="ndp-content-card">
            {renderContent()}
          </div>
        </div>

        {showLogoutModal && (
          <div className="logout-modal-overlay">
            <div className="logout-modal">
              <div className="logout-modal-header"><div className="logout-modal-icon">👋</div><h3>Confirm Logout</h3></div>
              <div className="logout-modal-body"><p>Are you sure you want to logout from <strong>NDP</strong> dashboard?</p><p className="logout-modal-warning">You will be redirected to the login page.</p></div>
              <div className="logout-modal-footer"><button className="logout-modal-cancel" onClick={handleCancelLogout}>Cancel</button><button className="logout-modal-confirm" onClick={handleConfirmLogout}>Yes, Logout</button></div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}